/* =====================================================================
   THE ROOM ENGINE — THE MARKS (E5)

   PLACEMENT (worker-safe, no DOM) — where every tuft of rough pasture,
   field seam, tree, wall, road, track and building goes, and where every
   landmark and bench mark stands, by survey-map.js's own rules, cited
   line by line. It runs in the worker beside the field and the
   lettering (E3's idiom) and arrives with them, so the main thread
   never does the arithmetic.

   DRAWING (main thread) — ONE INSTANCED DRAW PER FAMILY. Every instance
   is a point or a run in SHEET units plus a size, a seed and a band; the
   ground under it is read from the FIELD TEXTURE IN THE VERTEX SHADER,
   so nothing is looked up on the CPU and a pan moves no instance.

   Two rules from the sheet's own CSS decide what is held in which units:
     - the LAND FAMILIES are `vector-effect: non-scaling-stroke`, so the
       symbol's geometry is in MAP units (a tuft grows as you zoom) and
       only the pen's width is in device pixels — E2's ruling;
     - the LANDMARKS and the BENCH MARKS are `scale(1/var(--z))` and
       `scale(.95/var(--z))`, so they are the SAME SIZE ON SCREEN at
       every zoom and every tilt — rigid plates and upright sprites, out
       of one atlas cut at runtime (E3's idiom, `lm-art.js` the artwork).

   Every family fades in over one whole octave of zoom (E2's two
   generations) where the sheet switched a `lod-mid` / `lod-near` class
   on at a band boundary. Nothing pops.
   ===================================================================== */
(function (root) {
  'use strict';

  var MK = root.RoomMarks = root.RoomMarks || {};

  /* the six line-and-symbol families, in paint order. ONE DRAW EACH.
     `buildings` is the seventh and has its own program (a box that
     collapses to its own footprint at tilt 0). */
  var FAM = ['rough', 'fields', 'woods', 'walls', 'roads', 'tracks'];
  var FAMI = {}; FAM.forEach(function (f, i) { FAMI[f] = i; });
  MK.FAM = FAM; MK.FAMI = FAMI;

  /* the sheet's palette (styles.css :76-95) */
  var COL = {
    paper: [0.94509804, 0.90588235, 0.81960784],   // #f1e7d1
    paperHi: [0.96862745, 0.93725490, 0.86666667], // #f7efdd
    ink: [0.18039216, 0.14901961, 0.10980392],     // #2e261c
    ink2: [0.33333333, 0.28235294, 0.22745098],    // #55483a
    sepia: [0.60392157, 0.41960784, 0.25490196],   // #9a6b41
    water: [0.24705882, 0.49803922, 0.53333333],   // #3f7f88
    accent: [0.58823529, 0.19607843, 0.16470588]   // #96322a
  };
  MK.COL = COL;

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* survey-map.js:2694 — the jitter of anything scattered, verbatim */
  function jit(i, j, k) {
    var h = (i * 73856093) ^ (j * 19349663) ^ ((k || 0) * 83492791);
    h = (h ^ (h >>> 13)) * 1274126177;
    return (((h ^ (h >>> 16)) >>> 0) % 1000) / 1000;
  }
  MK.jit = jit;

  /* =================================================================
     PLACEMENT — the worker's half
     ================================================================= */
  function place(X) {
    var C = X.C, weeks = X.weeks, nWeeks = X.nWeeks, baseW = X.baseW;
    var fdata = X.field.data, FW = X.field.W, FH = X.field.H, lat = X.lat;
    var COAST = X.COAST, front = X.front, today = X.today;
    var EVENTS = X.events || {}, BANK = X.bankHolidays || {}, topo = X.topo || {};
    var POSTS = X.posts || [];
    var EVMODE = X.eventsMode || 'name';       /* R5 (b): ?events=name|track|rule */
    /* R70: sea level is five commits ON THE SHEET; on a record with its
       own ladder that is a smaller number of its own commits. `SEA_C` is
       that number, and it is what a raw count is compared against. */
    var SEA_C = (root.RoomField && root.RoomField.ladder)
      ? root.RoomField.ladder(X.scale).from(C.BASELINE_COMMITS) : C.BASELINE_COMMITS;
    var t0 = (root.performance || Date).now();

    function alongOf(t) { return C.px + t * C.cw; }
    function acrossOf(u) { return C.py + (u + C.EDGE_ROWS) * C.ch; }
    function rowOf(wi, di) { return (C.BOUSTROPHEDON && ((baseW + wi) & 1)) ? 6 - di : di; }
    function cell(wi, di) { return [alongOf(baseW + wi + 0.5), acrossOf(rowOf(wi, di) + 0.5)]; }
    function pt(t, u) { return [alongOf(t), acrossOf(u)]; }
    /* sheet units -> the field's own lattice, and a bilinear sample:
       the same arithmetic uvOf() does on the GPU (engine.js FIELD_GLSL) */
    function fieldAt(sx, sy) {
      var fx = clamp((sx - lat.X0) / lat.DX, 0, FW - 1.001);
      var fy = clamp((sy - lat.Y0) / lat.DY, 0, FH - 1.001);
      var x0 = fx | 0, y0 = fy | 0, tx = fx - x0, ty = fy - y0, i = y0 * FW + x0;
      return fdata[i] * (1 - tx) * (1 - ty) + fdata[i + 1] * tx * (1 - ty) +
             fdata[i + FW] * (1 - tx) * ty + fdata[i + FW + 1] * tx * ty;
    }
    /* survey-map.js:2674 — the slope in SHEET units, as the hachures take
       it: the central difference over one field cell, divided by the
       sheet units that cell covers (lat.DX = perWeek, lat.DY = perDay) */
    function slopeAt(sx, sy) {
      var gx = (fieldAt(sx + lat.DX, sy) - fieldAt(sx - lat.DX, sy)) / 2 / lat.DX;
      var gy = (fieldAt(sx, sy + lat.DY) - fieldAt(sx, sy - lat.DY)) / 2 / lat.DY;
      return Math.sqrt(gx * gx + gy * gy);
    }

    /* survey-map.js:2668 — the slopes are ABSOLUTE here, unlike the
       hachures': FLAT is about two dozen commits across a day row. */
    var FLAT_SLOPE = 0.028, WALL_SLOPE = 0.05;
    var L8 = Math.sqrt(8), L20 = Math.sqrt(20), L80 = Math.sqrt(80);
    var WATER = 0, ROUGH = 1, PASTURE = 2, STRIP = 3, WOOD = 4, STEEP = 5;
    function kindAt(sx, sy) {                       // survey-map.js:2681
      var v = fieldAt(sx, sy);
      if (v <= COAST + 0.02) return WATER;
      if (v < L8) return ROUGH;
      if (slopeAt(sx, sy) >= FLAT_SLOPE) return STEEP;
      if (v >= L80) return WOOD;
      if (v >= L20) return STRIP;
      return PASTURE;
    }

    /* the span the land is drawn over: the record's own weeks, cut at the
       survey front — nothing is farmed east of today (survey-map.js:2700) */
    var tFrom = (front.west - C.px) / C.cw;
    var frontT = front.none ? Infinity : (front.all ? (front.east - C.px) / C.cw
                                                     : (front.e0 - C.px) / C.cw);
    var tTo = Math.min((front.east - C.px) / C.cw, frontT + 1);
    tFrom = Math.round(tFrom); tTo = Math.round(tTo);
    var HOME_A0 = alongOf(0);        // the lattice's anchor: one field, one span
    var c0 = acrossOf(0), c1 = acrossOf(7);

    /* the instance buffers, pushed flat: x, y, dx, dy, size, seed, kind, band */
    var B = {}; FAM.forEach(function (f) { B[f] = []; });
    var STRIDE = 8;
    function push(fam, x, y, dx, dy, size, seed, kind, band) {
      var a = B[fam];
      a.push(x, y, dx, dy, size, seed, kind, band);
    }
    /* a RUN is a polyline: one instance to a segment. The shader knows a
       run from a symbol by kind >= 100. */
    function run(fam, pts, size, kind, band, seed) {
      for (var i = 0; i + 1 < pts.length; i++)
        push(fam, pts[i][0], pts[i][1], pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1],
             size, seed === undefined ? (i * 0.37) % 1 : seed, kind, band);
    }

    /* ==== R5 (b): A RUN'S QUAD IS A CHORD, SO A LONG ONE FLOATS.
       One instance spans its two ends, and the shader lifts only those
       two onto the ground: everything between is a STRAIGHT LINE ACROSS
       THE SCREEN. On the flat sheet that is exactly right and costs one
       instance; raised, a strip-field seam that runs the whole seven-day
       column (546 sheet units) leaves the ground at one end, sails over
       the hill between and comes back down at the other — Tommy's "weird
       thin black lines from north to south, only appear at tilt".
       `longRun` cuts such a line into pieces no longer than SUB_MAX and
       lifts every joint, so the line LIES ON THE GROUND and the depth
       test hides the part of it behind a hill.

       THE JOINTS MUST NOT DOUBLE THE INK. A run's quad overhangs its two
       ends by 1.5 pen widths so the line has a cap; abutted pieces would
       then overlap by three pen widths at every joint and, at an ink
       opacity of 0.62, print a bead there. A NEGATIVE size marks a piece
       of a subdivided run and its `seed` carries the cap mask — bit 1:
       no cap at the start, bit 2: no cap at the end — so only the two
       true ends of the whole line are capped and nothing is drawn twice.
       At tilt 0 the pieces are exactly collinear and the picture is the
       one before, to the pixel. ==== */
    var SUB_MAX = C.ch / 5;
    function longRun(fam, x0, y0, x1, y1, size, kind, band) {
      var dx = x1 - x0, dy = y1 - y0;
      var n = Math.ceil(Math.sqrt(dx * dx + dy * dy) / SUB_MAX);
      if (n <= 1) { push(fam, x0, y0, dx, dy, size, 0, kind, band); return; }
      for (var i = 0; i < n; i++) {
        var a = i / n;
        push(fam, x0 + dx * a, y0 + dy * a, dx / n, dy / n, -size,
             (i > 0 ? 1 : 0) + (i < n - 1 ? 2 : 0), kind, band);
      }
    }

    /* ==== R9 (b): A BOUNDARY THAT WANDERS, AND ONE THAT ABUTS.
       `curveRun` is `longRun` for a line that is already a polyline: the
       caller has walked the ground and the pieces are already short, so
       nothing is subdivided, but the SAME cap mask is carried (negative
       size, seed bits 1 and 2) so only the two true ends of the whole
       line are capped and no joint prints its ink twice. ==== */
    function curveRun(fam, pts, size, kind, band) {
      var n = pts.length - 1;
      if (n < 1) return;
      if (n === 1) {
        push(fam, pts[0][0], pts[0][1], pts[1][0] - pts[0][0], pts[1][1] - pts[0][1],
             size, 0, kind, band);
        return;
      }
      for (var i = 0; i < n; i++)
        push(fam, pts[i][0], pts[i][1], pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1],
             -size, (i > 0 ? 1 : 0) + (i < n - 1 ? 2 : 0), kind, band);
    }

    /* -------- 1. STRIP FIELDS (survey-map.js:2708) -----------------
       Each week's column is one strip: ridge-and-furrow along its length
       (the near band), a boundary at each column seam and a headland
       across the column where the cultivated stretch begins and ends
       (the mid band). */
    (function () {
      var STEP = 0.05, FURROWS = 5;
      /* ==== R9 (b): THE SEAM IS A FIELD BOUNDARY, NOT A COLUMN RULE.
         It was one dead-straight line down the whole contiguous stretch
         of strip in a column — up to seven day rows, 546 sheet units —
         so raised it crossed two hills and a valley as a ruled diagonal
         and read as a fence a mile long (room-rev2/s-zoom400-t55.png).
         A boundary on a survey sheet is SHORT, it LEANS with the lie of
         the land, and it STOPS at the next one.

         So the seam is walked, not drawn: at each step the contour's own
         tangent at the point (perpendicular to the field's gradient, the
         same central difference `slopeAt` takes) is blended with straight
         down the column and the bearing is clamped to LEAN_MAX, so the
         line follows the ground as far as a boundary can and never turns
         into a contour. It ENDS at the first ordinary contour it crosses
         (`LV_STEP`, the sheet's own interval, sqrt(5)/8) or at SEAM_MAX,
         whichever comes first, and the next piece starts a gap later. A
         piece shorter than SEAM_MIN is not drawn at all.
         `?seams=old` puts the ruled version back for the pair. ==== */
      var LV_STEP = Math.sqrt(5) / 8;
      var SEAM_MAX = C.ch * 1.15;      // about one day row: a field's length
      var SEAM_MIN = C.ch * 0.22;
      var SEAM_GAP = 0.22;             // in rows, between one piece and the next
      var LEAN_MAX = 0.36;             // dx per dy — about 20 degrees off the column
      var LEAN_W = 0.45;               // how much of the contour's tangent is taken
      var SEAM_OLD = X.seams === 'old';
      function contourDown(sx, sy) {
        /* the contour's unit tangent, turned to point DOWN the column */
        var gx = (fieldAt(sx + lat.DX, sy) - fieldAt(sx - lat.DX, sy)) / 2 / lat.DX;
        var gy = (fieldAt(sx, sy + lat.DY) - fieldAt(sx, sy - lat.DY)) / 2 / lat.DY;
        var tx = -gy, ty = gx, l = Math.hypot(tx, ty);
        if (l < 1e-9) return [0, 1];
        tx /= l; ty /= l;
        return ty < 0 ? [-tx, -ty] : [tx, ty];
      }
      function seam(t, u0, u1) {
        if (SEAM_OLD) {
          var a0 = pt(t, u0), b0 = pt(t, u1);
          longRun('fields', a0[0], a0[1], b0[0], b0[1], 0.7, 100, 1);
          return;
        }
        var x = alongOf(t), y = acrossOf(u0), du = 0.05;
        var pts = [[x, y]], run = 0, lv = Math.floor(fieldAt(x, y) / LV_STEP);
        var u = u0, k = 0;
        function flush() {
          if (run >= SEAM_MIN && pts.length > 1) curveRun('fields', pts, 0.55, 100, 2);
          pts = []; run = 0;
        }
        while (u < u1) {
          var T = contourDown(x, y);
          var lean = clamp(T[0] / Math.max(T[1], 0.2), -LEAN_MAX / LEAN_W, LEAN_MAX / LEAN_W) * LEAN_W;
          var dy = C.ch * du;
          x += lean * dy; y += dy; u += du; k++;
          run += Math.hypot(lean * dy, dy);
          var lv2 = Math.floor(fieldAt(x, y) / LV_STEP);
          var end = lv2 !== lv || run >= SEAM_MAX || u >= u1;
          if (k % 2 === 0 || end) pts.push([x, y]);
          if (!end) continue;
          lv = lv2;
          flush();
          /* the gap, and the next piece starts back on its own column */
          u += SEAM_GAP; if (u >= u1) break;
          x = alongOf(t) + (jit(t | 0, k, 7) - 0.5) * C.cw * 0.10;
          y = acrossOf(u);
          lv = Math.floor(fieldAt(x, y) / LV_STEP);
          pts = [[x, y]];
        }
        flush();
      }
      function runsAlong(t) {
        var out = [], cur = null;
        for (var u = 0.02; u <= 6.98; u += STEP) {
          var q = pt(t, u);
          if (kindAt(q[0], q[1]) === STRIP) { if (cur) cur.b = u; else cur = { a: u, b: u }; }
          else if (cur) { out.push(cur); cur = null; }
        }
        if (cur) out.push(cur);
        return out.filter(function (r) { return r.b - r.a >= 0.2; });
      }
      for (var t = tFrom; t < tTo; t++) {
        for (var i = 0; i < FURROWS; i++) {
          var tf = t + (i + 0.5) / FURROWS;
          runsAlong(tf).forEach(function (r) {
            var a = pt(tf, r.a), b = pt(tf, r.b);
            longRun('fields', a[0], a[1], b[0], b[1], 0.5, 102, 2);   // furrow, lod-near
          });
        }
        runsAlong(t + 0.5).forEach(function (r) {
          [r.a, r.b].forEach(function (u) {
            var a = pt(t, u), b = pt(t + 1, u);
            longRun('fields', a[0], a[1], b[0], b[1], 0.5, 101, 2);    // headland
          });
        });
        var cur = null;
        for (var u2 = 0.02; u2 <= 6.98; u2 += STEP) {
          var qa = pt(t - 0.04, u2), qb = pt(t + 0.04, u2);
          var on = kindAt(qa[0], qa[1]) === STRIP || kindAt(qb[0], qb[1]) === STRIP;
          if (on) { if (cur) cur.b = u2; else cur = { a: u2, b: u2 }; }
          else if (cur) {
            if (cur.b - cur.a >= 0.2) seam(t, cur.a, cur.b);   // the seam
            cur = null;
          }
        }
        if (cur && cur.b - cur.a >= 0.2) seam(t, cur.a, cur.b);
      }
    })();

    /* -------- 2. ROUGH PASTURE (survey-map.js:2752) ----------------
       The lowland green between the coast and eight commits, tufted the
       way the marsh is, thinly and at a jitter, on a 9 x 9 lattice that
       passes through the sheet's own edge so the tufts never travel. */
    (function () {
      var STEPX = 9, STEPY = 9;
      var a1 = alongOf(tTo);
      var ni = Math.ceil((alongOf(tFrom) - HOME_A0) / STEPX);
      for (var a = HOME_A0 + ni * STEPX; a < a1; a += STEPX, ni++) {
        var nj = 0;
        for (var c = c0 + 3; c < c1 - 3; c += STEPY, nj++) {
          if (jit(ni, nj, 1) > 0.5) continue;
          var x = a + (jit(ni, nj, 2) - 0.5) * STEPX * 0.8;
          var y = c + (jit(ni, nj, 3) - 0.5) * STEPY * 0.8;
          if (kindAt(x, y) !== ROUGH) continue;
          /* the tuft is 3.8 map units across and 1.8 deep — three rules,
             cut in the fragment shader from the same figure the sheet
             draws (`M-1.9 -0.9h3.8 M-1.1 0h2.2 M-1.9 0.9h3.8`) */
          push('rough', x, y, 1, 0, 3.8, jit(ni, nj, 6), 0, 1);
        }
      }
    })();

    /* -------- 3. WOODS (survey-map.js:2795) ------------------------
       A mask of the wooded ground, lightly smoothed so the wood edge is
       a curve, its half-level traced as a SCALLOPED edge; inside it the
       tree symbols on a jittered lattice.
       ==== R36: AND THE SPECIES IS A RULE OF THE GROUND, NOT A DATE.
       It was deciduous west of a fault date and conifer east of it,
       which is an era by another name. Two rules of the ground were
       tried. HEIGHT does not work on this record: the wood only stands
       above the 80 contour to begin with, the next rung up is 125, and
       the highest ground on the sheet is 114 (R26's own note) — so a
       125 line paints every tree deciduous and the conifer is never cut
       (measured: room-rev6/R36-*-woods-east.png). What DOES divide the
       wooded ground is the fall under it: the mask takes everything
       flatter than FLAT_SLOPE, and inside that there is a range. So
       plantation on the ROUGHER half of the wooded tops, deciduous on
       the smoothest — the convention of any hill sheet, where the
       broadleaf is on the ground worth keeping and the plantation goes
       on what is left, and it moves with the record instead of a date. */
    var woodMask = null;
    (function () {
      var m = new Float32Array(FW * FH), any = false;
      for (var y = 1; y < FH - 1; y++) {
        for (var x = 1; x < FW - 1; x++) {
          var v = fdata[y * FW + x];
          if (v <= COAST + 0.02 || v < L80) continue;
          var sx = lat.X0 + x * lat.DX, sy = lat.Y0 + y * lat.DY;
          if (slopeAt(sx, sy) >= FLAT_SLOPE) continue;
          m[y * FW + x] = 1; any = true;
        }
      }
      if (!any) return;
      woodMask = blur(m, FW, FH, 2, 5);   // ux*0.28 and uy*0.28, as `smooth` rounds them
      function maskAt(sx, sy) {
        var fx = clamp((sx - lat.X0) / lat.DX, 0, FW - 1.001);
        var fy = clamp((sy - lat.Y0) / lat.DY, 0, FH - 1.001);
        var x0 = fx | 0, y0 = fy | 0, tx = fx - x0, ty = fy - y0, i = y0 * FW + x0;
        return woodMask[i] * (1 - tx) * (1 - ty) + woodMask[i + 1] * tx * (1 - ty) +
               woodMask[i + FW] * (1 - tx) * ty + woodMask[i + FW + 1] * tx * ty;
      }
      /* the scalloped edge: the half-level of the mask, in segments, each
         bumped on the side the wood is NOT — the gradient says which */
      segmentsAt(woodMask, FW, FH, 0.5, lat, 2).forEach(function (s) {
        var mx = (s[0] + s[2]) / 2, my = (s[1] + s[3]) / 2;
        var gx = maskAt(mx + lat.DX, my) - maskAt(mx - lat.DX, my);
        var gy = maskAt(mx, my + lat.DY) - maskAt(mx, my - lat.DY);
        var dx = s[2] - s[0], dy = s[3] - s[1];
        /* the scallop bulges away from the wood: the cross product of the
           segment with the inward gradient decides the sweep */
        var sweep = (dx * gy - dy * gx) > 0 ? 1 : 0;
        push('woods', s[0], s[1], dx, dy, 0.7, sweep, 103, 1);
      });
      var STEPX = 5.6, STEPY = 5.6, a1 = alongOf(tTo);
      var ni = Math.ceil((alongOf(tFrom) - HOME_A0) / STEPX);
      for (var a = HOME_A0 + ni * STEPX; a < a1; a += STEPX, ni++) {
        var nj = 0;
        for (var c = c0 + 2; c < c1 - 2; c += STEPY, nj++) {
          var x = a + (jit(ni, nj, 4) - 0.5) * STEPX * 0.9;
          var y = c + (jit(ni, nj, 5) - 0.5) * STEPY * 0.9;
          if (maskAt(x, y) < 0.62) continue;
          /* R36: deciduous on the smoothest tops, plantation on the
             rougher half of them (survey-map.js:2846) */
          push('woods', x, y, 1, 0, 6.0, jit(ni, nj, 7),
               slopeAt(x, y) >= FLAT_SLOPE * 0.5 ? 1 : 0, 1);
        }
      }
    })();

    /* -------- 4. WALLS (survey-map.js:2861) ------------------------
       Where the hachures are heaviest a contour break is a wall: every
       other tint level is walked and the stretches on the steepest
       ground are inked as short lengths of dry-stone wall. */
    (function () {
      var STEP = Math.sqrt(5) / 8, maxV = X.maxHeight + COAST;
      for (var li = 4; li * STEP < maxV; li += 4) {
        var lv = li * STEP;
        if (lv <= COAST * 1.02) continue;
        segmentsAt(fdata, FW, FH, lv, lat, 2).forEach(function (s) {
          var mx = (s[0] + s[2]) / 2, my = (s[1] + s[3]) / 2;
          if (mx < alongOf(tFrom) || mx >= alongOf(tTo)) return;
          if (fieldAt(mx, my) <= COAST + 0.3) return;
          if (slopeAt(mx, my) < WALL_SLOPE) return;
          push('walls', s[0], s[1], s[2] - s[0], s[3] - s[1], 1.3, 0, 100, 1);
        });
      }
    })();

    /* -------- 5. THE COAST ROAD (survey-map.js:2905) ---------------
       At every step along the strip the first ground above the coast,
       walking in from The North Water, is the shore, and the road runs
       a little inland of it. A dip narrower than a column is a bay's
       mouth: the line is OPENED and the road crosses it — by a FORD if
       the mouth is under half a column, by a BRIDGE otherwise. */
    var roadAt = function () { return null; };
    /* THE COAST ROAD IS DROPPED — Tommy, 2026-09-14 ~03:15, on seeing it at the pitched
       pose as a string of pale sticks along every shore (#65,407,510,40,202): asked
       whether it was a deliberate road between settlements and told it followed the
       shore by construction, "drop it for now". The laying code stays (ROADS false)
       because a road BETWEEN SETTLEMENTS is the future design, and its fords and bridges
       are the parts worth keeping; the 1 January milestones fall back to their own
       search when roadAt is null, as they did before the road ran past them. */
    var ROADS = false;
    if (ROADS) (function () {
      var STEP = 0.1, INLAND = 0.11, samples = [];
      for (var t = tFrom + STEP / 2; t < tTo; t += STEP) {
        var shore = null;
        for (var u = 0; u <= 6.5; u += 0.05) {
          var q = pt(t, u);
          if (fieldAt(q[0], q[1]) > COAST + 0.08) { shore = u; break; }
        }
        samples.push({ t: t, u: shore === null ? null : Math.min(6.6, shore + INLAND) });
      }
      var R = 5, n = samples.length;
      var raw = samples.map(function (s) { return s.u === null ? 7.4 : s.u; });
      var mn = new Array(n), op = new Array(n), i, k;
      for (i = 0; i < n; i++) { var m = Infinity; for (k = Math.max(0, i - R); k <= Math.min(n - 1, i + R); k++) if (raw[k] < m) m = raw[k]; mn[i] = m; }
      for (i = 0; i < n; i++) { var M = -Infinity; for (k = Math.max(0, i - R); k <= Math.min(n - 1, i + R); k++) if (mn[k] > M) M = mn[k]; op[i] = M; }
      samples.forEach(function (s, i3) {
        s.cross = op[i3] < raw[i3] - 0.15 && op[i3] < 6.7;
        s.u = op[i3] < 6.7 ? op[i3] : null;
      });
      for (var pass = 0; pass < 2; pass++) {
        var out = samples.map(function (s) { return s.u; });
        for (var i4 = 1; i4 < n - 1; i4++) {
          var a = samples[i4 - 1].u, b = samples[i4].u, c = samples[i4 + 1].u;
          if (a === null || b === null || c === null) continue;
          out[i4] = (a + 2 * b + c) / 4;
        }
        samples.forEach(function (s, i5) { s.u = out[i5]; });
      }
      var runs2 = [], cur = null;
      samples.forEach(function (s, i6) {
        if (s.u !== null) { if (cur) cur.b = i6; else cur = { a: i6, b: i6 }; }
        else if (cur) { runs2.push(cur); cur = null; }
      });
      if (cur) runs2.push(cur);
      runs2 = runs2.filter(function (r) { return r.b - r.a >= 2; });
      function seg(i0, i1, kind) {
        var ps = [];
        for (var i7 = i0; i7 <= i1; i7++) ps.push(pt(samples[i7].t, samples[i7].u));
        run('roads', ps, kind === 102 ? 1 : 2.6, kind, 1);
      }
      runs2.forEach(function (r) {
        var i8 = r.a;
        while (i8 <= r.b) {
          var j = i8;
          while (j + 1 <= r.b && samples[j + 1].cross === samples[i8].cross) j++;
          if (!samples[i8].cross) seg(i8, j, 100);
          else {
            var a0 = Math.max(r.a, i8 - 1), b0 = Math.min(r.b, j + 1);
            var wide = (j - i8 + 1) * STEP;
            if (wide <= 0.5) seg(a0, b0, 102);        // a ford
            else {
              seg(a0, b0, 100);
              var p0 = pt(samples[a0].t, samples[a0].u), p1 = pt(samples[b0].t, samples[b0].u);
              var mx = (p0[0] + p1[0]) / 2, my = (p0[1] + p1[1]) / 2;
              var dx = p1[0] - p0[0], dy = p1[1] - p0[1], L = Math.sqrt(dx * dx + dy * dy) || 1;
              var nxv = -dy / L, nyv = dx / L, ax = dx / L, ay = dy / L;
              [-1, 1].forEach(function (side) {       // the bridge's two parapets
                var cx = mx + nxv * side * 2.2, cy = my + nyv * side * 2.2;
                push('roads', cx - ax * 3.6, cy - ay * 3.6, ax * 7.2, ay * 7.2, 1.1, 0, 103, 1);
              });
            }
          }
          i8 = j + 1;
        }
      });
      roadAt = function (t) {
        var i = Math.round((t - tFrom - STEP / 2) / STEP);
        if (i < 0 || i >= samples.length) return null;
        var s = samples[i];
        if (s.u === null) return null;
        for (var k2 = 0; k2 < runs2.length; k2++) if (i >= runs2[k2].a && i <= runs2[k2].b) return s.u;
        return null;
      };
    })();

    /* -------- the reservations: nothing sits on anything else -------
       survey-map.js:3116, the same boxes the lettering uses. The
       lettering is placed AFTER the marks in survey-map.js and claims
       what is left, so E5's list is handed back to E3 through
       `field.js` and the two agree about the ground. */
    var occupied = [];
    function hits(b) {
      for (var i = 0; i < occupied.length; i++) {
        var o = occupied[i];
        if (!(b.x2 < o.x1 - 6 || b.x1 > o.x2 + 6 || b.y2 < o.y1 - 2 || b.y1 > o.y2 + 2)) return true;
      }
      return false;
    }
    function reserve(b) { occupied.push(b); return b; }

    var BUILD = [], landmarks = [], plates = [];
    var ART = root.RoomLmArt;

    /* -------- THE BENCH MARKS CLAIM THEIR GROUND FIRST -------------
       survey-map.js:3145: the order is bench marks, then landmarks, then
       the lettering, and each takes what is left. The glyph and its
       numeral are held at one apparent size, so what they cover on the
       GROUND is the screen box divided by the zoom the sheet is laid at. */
    /* ==== R11 (b): THE BOX IS THE PLATE THAT IS ACTUALLY DRAWN.
       The figures were hand-copied (-10/+30, -12/+8) and came out SMALLER
       than the quad the sprite atlas cuts, so the year figure's own box
       could clear the reservation while the ink sat on the broad arrow
       (REPORT-room-R5's OPEN, room-rev2/merged5-fit.png). The one truth
       is `BOX` below, the table `buildAtlas` lays the cell out from: the
       plate is `BOX.bm` CSS px about the anchor, and what it covers on
       the GROUND is that divided by the zoom the sheet is laid at. The
       camp reserved nothing at all — a figure could print on the survey
       party's tent as easily as on a bench mark — so it claims its own
       plate the same way. ==== */
    var invK = 1 / (X.k || 1);
    function plateBox(cx, cy, sym) {
      var b = BOX[sym] || BOX.trig;
      return { x1: cx + b.x0 * b.k * invK, x2: cx + b.x1 * b.k * invK,
               y1: cy + b.y0 * b.k * invK, y2: cy + b.y1 * b.k * invK };
    }
    POSTS.forEach(function (p) {
      var pos = dateIdx(p.date);
      if (!pos) return;
      var c = cell(pos[0], pos[1]);
      reserve(plateBox(c[0], c[1], 'bm'));
      plates.push({ x: c[0], y: c[1], sym: 'bm', text: p.numeral || '', band: 0 });
    });

    /* -------- 6. THE BUILDINGS (survey-map.js:3019, 4126) ----------
       A rule over the calendar and the two levels: a week is STEADY
       when every working day stands above the level of a typed day and
       their mean stands within a quarter of the level of a directed
       one; a RUN is consecutive steady weeks; and a run is settled by
       its length — three weeks a barn, six a farmstead, twelve a
       hamlet, a quarter a village. */
    /* THE LEVEL OF A DIRECTED DAY (survey-map.js:1456): the MEDIAN of
       every weekday count since the datum horizon that is not a bank
       holiday and not leave (R36: the horizon, not a fault date). It is
       the level the settlement rule measures against. */
    function newSeriesCommits() {
      var dH = (X.horizons || []).filter(function (h) { return h.datum; })[0];
      var from = dH ? dH.date : '0000-00-00';
      var vals = [];
      for (var w = 0; w < nWeeks; w++) for (var d = 0; d < 7; d++) {
        var dy = weeks[w][d];
        if (!dy.inRange || !dy.count) continue;
        if (dy.date <= from || dy.date > today) continue;
        if ((d + 6) % 7 >= 5) continue;                 // Saturday and Sunday
        if (BANK[dy.date]) continue;
        var ev = EVENTS[dy.date];
        if (ev && ev.type === 'holiday') continue;
        vals.push(dy.count);
      }
      if (!vals.length) return C.BASELINE_COMMITS;
      vals.sort(function (a, b) { return a - b; });
      return vals[vals.length >> 1];
    }

    function addBuilding(x, y, kind, rot, water, noPlan) {
      if (!ART || !ART.BUILDINGS[kind]) return null;
      var onLand = fieldAt(x, y) > COAST + 0.1;
      if (!onLand && !water) return null;
      BUILD.push({ x: x, y: y, kind: kind, rot: rot || 0, water: !onLand, noPlan: !!noPlan });
      return true;
    }
    /* -------- 7. THE LANDMARKS (survey-map.js:3419) ----------------
       On the shore of its own bay, or afloat, or (a lighthouse) on a
       skerry. v23: no holiday landmark stands on a skerry any more. */
    var zF = X.k || 1;
    var frontAlong = front.none ? Infinity : (front.all ? front.east : front.e1);
    var LM_UNITS = 1.85 / (zF * 3.4);     // one landmark ART unit in sheet units at the near band's far edge
    function lmFootprint(x, y, w) {
      return { x1: x - (w + 2) * LM_UNITS, x2: x + (w + 2) * LM_UNITS,
               y1: y - 12 * LM_UNITS, y2: y + 8 * LM_UNITS };
    }
    function standingPlace(x, y, key) {
      var lm = (ART && ART.LANDMARKS[key]) || { w: 5 };
      var out;
      if (fieldAt(x, y) > COAST) out = { x: x, y: y, mode: 'land' };
      else if (lm.floats) out = { x: x, y: y, mode: 'afloat' };
      else if (key === 'lighthouse') out = { x: x, y: y, mode: 'skerry' };
      else {
        var R = 3.2, STEP = 0.2, cands = [];
        for (var i = -R; i <= R; i += STEP) {
          for (var j = -R; j <= R; j += STEP) {
            var px = x + i * C.cw, py = y + j * C.ch;
            if (py < c0 || py > c1 || px > frontAlong) continue;
            if (fieldAt(px, py) <= COAST + 0.12) continue;
            cands.push({ x: px, y: py, d: i * i + j * j });
          }
        }
        cands.sort(function (a, b) { return a.d - b.d; });
        var pick = null;
        for (var k = 0; k < cands.length && k < 60; k++) {
          var c = cands[k];
          var dx = c.x - x, dy = c.y - y, m = Math.sqrt(dx * dx + dy * dy) || 1;
          var nx = c.x + (dx / m) * lm.w * 0.5 * LM_UNITS;
          var ny = c.y + (dy / m) * lm.w * 0.5 * LM_UNITS;
          if (fieldAt(nx, ny) <= COAST) { nx = c.x; ny = c.y; }
          if (!pick) pick = { x: nx, y: ny };
          if (!hits(lmFootprint(nx, ny, lm.w))) { pick = { x: nx, y: ny }; break; }
        }
        out = pick ? { x: pick.x, y: pick.y, mode: 'shore' } : { x: x, y: y, mode: 'afloat' };
      }
      /* A BUILDING STANDS ON LEVEL GROUND (v25, E) */
      if (ART && ART.LM_BUILDING[key] && out.mode !== 'afloat' && out.mode !== 'skerry' &&
          slopeAt(out.x, out.y) >= WALL_SLOPE) {
        var R2 = 2, ST = 0.25, flat = [];
        for (var ii = -R2; ii <= R2; ii += ST) {
          for (var jj = -R2; jj <= R2; jj += ST) {
            var qx = out.x + ii * C.cw, qy = out.y + jj * C.ch;
            if (qy < c0 || qy > c1 || qx > frontAlong) continue;
            if (fieldAt(qx, qy) <= COAST + 0.12) continue;
            if (slopeAt(qx, qy) >= WALL_SLOPE) continue;
            if (hits(lmFootprint(qx, qy, lm.w))) continue;
            flat.push({ x: qx, y: qy, d: ii * ii + jj * jj });
          }
        }
        if (flat.length) { flat.sort(function (a, b) { return a.d - b.d; }); out = { x: flat[0].x, y: flat[0].y, mode: out.mode }; }
      }
      reserve(lmFootprint(out.x, out.y, lm.w));
      return out;
    }
    /* LH2: `date` is the day the landmark stands for, when it stands for
       one — day.js reads it so the hand on a landmark reads ITS day, not
       the ground's (the landmark is walked to the shore off a day that is
       sea, so the ground under it is another day) */
    function standLandmark(key, x, y, band, date) {
      if (!ART || !ART.LANDMARKS[key]) return null;
      var at = standingPlace(x, y, key);
      var mode = at.mode === 'shore' ? 'land' : at.mode;
      var lm = ART.LANDMARKS[key];
      if (lm.bed) mode = 'bed';
      else if (lm.floats && mode !== 'land') mode = 'afloat';
      landmarks.push({ x: at.x, y: at.y, key: key, mode: mode, band: band === undefined ? 2 : band,
                       afloat: mode === 'afloat' || mode === 'bed', date: date || null });
      if (ART.LM_BUILDING[key] && mode !== 'afloat')
        addBuilding(at.x, at.y, ART.LM_BUILDING[key], 0, mode === 'skerry');
      return at;
    }

    /* the runs of the record (survey-map.js:1379, as E3 ported it) */
    var flat = [];
    for (var wi = 0; wi < nWeeks; wi++) for (var di = 0; di < 7; di++)
      flat.push({ d: weeks[wi][di], wi: wi, di: di });
    function runsOf(type) {
      var o = [], cur = null;
      flat.forEach(function (c, i) {
        var ev = EVENTS[c.d.date];
        if (ev && ev.type === type && c.d.inRange) {
          if (cur && cur.lastIdx === i - 1 && (type !== 'work-trip' || cur.names[0] === ev.name)) {
            cur.cells.push(c); cur.lastIdx = i;
            if (cur.names.indexOf(ev.name) < 0) cur.names.push(ev.name);
          } else { cur = { names: [ev.name], cells: [c], lastIdx: i }; o.push(cur); }
        } else cur = null;
      });
      return o;
    }
    function hasShore(cells) {
      for (var i = 0; i < cells.length; i++) {
        var q = cell(cells[i].wi, cells[i].di);
        for (var a = -1; a <= 1; a++) for (var b = -1; b <= 1; b++) {
          if (!a && !b) continue;
          if (fieldAt(q[0] + a * C.cw, q[1] + b * C.ch) > COAST) return true;
        }
      }
      return false;
    }
    var LM_DEFAULT = (ART && ART.LM_DEFAULT) || {};
    /* ONE LANDMARK TO AN EVENT, not to a run (survey-map.js:3527) */
    function landmarkForRun(r, type) {
      var segs = [], taken = [];
      r.cells.forEach(function (c) {
        var ev = EVENTS[c.d.date] || {};
        var last = segs[segs.length - 1];
        if (last && last.name === ev.name) last.cells.push(c);
        else segs.push({ name: ev.name, landmark: ev.landmark, cells: [c] });
      });
      segs.forEach(function (sg) {
        var key = sg.landmark || LM_DEFAULT[type];
        if (!sg.landmark && type === 'holiday' && sg.cells.length >= 4) key = 'house';
        var mid = sg.cells[Math.floor(sg.cells.length / 2)];
        taken.push(mid);
        var c = cell(mid.wi, mid.di);
        standLandmark(key, c[0], c[1], undefined, mid.d.date);
      });
      return taken;
    }

    /* the holiday bays, and the wreck at the deepest free sounding */
    runsOf('holiday').forEach(function (r) {
      var taken = landmarkForRun(r, 'holiday');
      if (hasShore(r.cells)) {
        /* ==== HL1 (2026-09-29): THE HOUSE STANDS AT THE LANDING, ON THE
           SHORE. Tommy: "where are the house logos for holidays meant to
           sit? most are just in the sea?" They were set on the run's first
           DAY, and a day of leave is sea — the old plate's rule was that
           the vignette is cut at a landing place ("I don't hate the house
           but when all the land is below sea in 2025 it's just a house in
           the sea", survey-map.js:3566). So from the first day the nearest
           ground a cap clear of the waterline is found, nearer days of the
           run first, and the house stands there; a run with no such ground
           within reach sets no house (its name on the water is the
           record). ==== */
        var head = null;
        for (var hi = 0; hi < r.cells.length && !head; hi++) {
          var hc = cell(r.cells[hi].wi, r.cells[hi].di), best = null;
          for (var hx = -1.6; hx <= 1.6; hx += 0.2) for (var hy = -1.6; hy <= 1.6; hy += 0.2) {
            var qx = hc[0] + hx * C.cw, qy = hc[1] + hy * C.ch;
            if (qy < c0 || qy > c1 || qx > frontAlong) continue;
            if (fieldAt(qx, qy) <= COAST + 0.12) continue;
            var dq = hx * hx + hy * hy;
            if (!best || dq < best.d) best = { x: qx, y: qy, d: dq };
          }
          if (best) head = [best.x, best.y];
        }
        if (!head) return;
        reserve(plateBox(head[0], head[1], 'vig'));   /* R11 (b): the same table */
        plates.push({ x: head[0], y: head[1], sym: 'vig',
                      key: r.cells.length >= 4 ? 'house' : 'tent',
                      text: r.cells.length >= 4 ? 'house' : 'tent', band: 3 });
        return;
      }
      if (topo.deeps && topo.deeps[r.names[0]] && r.cells.length > 1) {
        var lowest = null, lowV = Infinity;
        r.cells.forEach(function (cc) {
          if (taken.indexOf(cc) >= 0) return;
          var q = cell(cc.wi, cc.di), v = fieldAt(q[0], q[1]);
          if (v < lowV) { lowV = v; lowest = cc; }
        });
        if (lowest) {
          var wq = cell(lowest.wi, lowest.di), e = lat.DX * 0.5;
          var gx = fieldAt(wq[0] + e, wq[1]) - fieldAt(wq[0] - e, wq[1]);
          var gy = fieldAt(wq[0], wq[1] + e) - fieldAt(wq[0], wq[1] - e);
          var fall = (gx || gy) ? Math.atan2(-gy, -gx) * 180 / Math.PI : 0;
          landmarks.push({ x: wq[0], y: wq[1], key: 'wreck', mode: 'bed', band: 2,
                           angle: fall + 22, afloat: true });
        }
      }
    });

    /* the tracks: a work trip is a journey across a seam (v23, E) */
    runsOf('work-trip').forEach(function (r) {
      landmarkForRun(r, 'work-trip');
      var pts = r.cells.map(function (c) { return cell(c.wi, c.di); });
      var cols = {}; r.cells.forEach(function (c) { cols[c.wi] = 1; });
      /* ==== R5 (b): THE WORK EVENT'S LINE. A trip's cells are days, and
         a trip that crosses a week takes the track from one column to
         the next — on the flat sheet a journey, under the camera a long
         straight hair over the sea (Tommy: "the line concept we use for
         work events like AWS re:Invent etc. doesn't really work any
         more"). `?events=`:
           name  (the default) — no line at all. The trip keeps its
                 landmark, which stands on its own ground, and its name,
                 which is lettered on the ground beside it (E3). That is
                 what an event IS on this sheet.
           track — the dashed track, cut into pieces that lie on the
                 ground, with its two nodes.
           rule  — E5's one long chord, kept so the veto is a flip.
         Whichever is drawn, the name and the landmark are unchanged. */
      if (Object.keys(cols).length > 1 && EVMODE !== 'name') {
        if (EVMODE === 'rule') run('tracks', pts, 1.1, 100, 1);
        else for (var pi = 0; pi + 1 < pts.length; pi++)
          longRun('tracks', pts[pi][0], pts[pi][1], pts[pi + 1][0], pts[pi + 1][1], 1.1, 100, 1);
        [pts[0], pts[pts.length - 1]].forEach(function (p) {
          push('tracks', p[0], p[1], 1, 0, 4.4, 0, 10, 1);  // the node, in DEVICE px
        });
      }
    });

    /* the one-day kinds, their point symbols and their landmarks */
    for (var a2 = 0; a2 < nWeeks; a2++) for (var b2 = 0; b2 < 7; b2++) {
      var dy2 = weeks[a2][b2];
      if (!dy2.inRange || dy2.date > today) continue;
      var ev2 = EVENTS[dy2.date], cc2 = cell(a2, b2);
      var one = ev2 && (ev2.type === 'quarterly-planning' || ev2.type === 'roundtable' || ev2.type === 'aws-summit' || ev2.type === 'conference');
      if (one) standLandmark(ev2.landmark || LM_DEFAULT[ev2.type], cc2[0], cc2[1], undefined, dy2.date);
      if (ev2 && (ev2.type === 'quarterly-planning' || ev2.type === 'roundtable'))
        plates.push({ x: cc2[0], y: cc2[1], sym: 'trig', band: 3 });   // lod-wide: gone close in
      else if (ev2 && (ev2.type === 'aws-summit' || ev2.type === 'conference'))   /* VS1: any other conference is drawn as a summit is */
        plates.push({ x: cc2[0], y: cc2[1], sym: 'beacon', band: 3 });
      else if (!ev2 && dy2.count === 0 && BANK[dy2.date]) {
        var shore = hasShore([{ wi: a2, di: b2 }]);
        plates.push({ x: cc2[0], y: cc2[1], sym: 'marsh', band: shore ? 3 : 0 });
        if (shore) standLandmark(LM_DEFAULT['bank-holiday'], cc2[0], cc2[1], undefined, dy2.date);
      }
    }

    /* the cairns on the tops the sheet already carries (survey-map.js:3855) */
    (function () {
      var CAIRN_LEVEL = 45;
      for (var a = 1; a <= nWeeks - 2; a++) {
        for (var b = 0; b < 7; b++) {
          var d0 = weeks[a][b];
          if (!d0.inRange || d0.date > today || d0.count <= CAIRN_LEVEL) continue;
          if (EVENTS[d0.date]) continue;
          var isMax = true;
          for (var i = -1; i <= 1 && isMax; i++) {
            for (var j = -1; j <= 1; j++) {
              if (!i && !j) continue;
              var dd = b + j; if (dd < 0 || dd > 6) continue;
              if (weeks[a + i][dd].count >= d0.count) { isMax = false; break; }
            }
          }
          if (!isMax) continue;
          var cq = cell(a, b);
          standLandmark('cairn', cq[0], cq[1]);
        }
      }
    })();

    /* THE SHIP, ON THE WIDEST OPEN WATER (survey-map.js:3040). A county
       map of 1610 filled its water with a rose and a ship. The rose is
       printed on the sheet; the ship belongs to the sea, and the sea
       moves — she floats at the datum with her ripple under her (v23,
       C), at the middle band and in. */
    (function () {
      var R = 50;
      var a0 = alongOf(tFrom) + R + 10, a1 = alongOf(tTo) - R - 10;
      var ca = c0 + R * 0.4, cb = c1 - R * 0.4;
      function clearance(sx, sy, rad) {
        var worst = 1e9;
        for (var k = 0; k < 12; k++) {
          var an = k / 12 * Math.PI * 2;
          for (var rr = 0.5; rr <= 1.001; rr += 0.5) {
            var dep = COAST - fieldAt(sx + Math.cos(an) * rad * rr, sy + Math.sin(an) * rad * rr);
            if (dep < worst) worst = dep;
          }
        }
        return worst;
      }
      var best = null;
      for (var aa = a0; aa <= a1; aa += 13) {
        for (var cc = ca; cc <= cb; cc += 9) {
          var v = clearance(aa, cc, 24);
          if (!best || v > best[2]) best = [aa, cc, v];
        }
      }
      if (!best || best[2] < 0.02) return;
      landmarks.push({ x: best[0], y: best[1], key: 'ship', mode: 'afloat', band: 1, afloat: true });
    })();

    /* one whale in the Precambrian sea, and she moves (survey-map.js:3881) */
    (function () {
      var spans = Object.keys(topo.oceans || {});
      if (!spans.length) return;
      var parts = spans[0].split('..');
      var A = dateIdx(parts[0]), Bp = dateIdx(parts[parts.length - 1]);
      if (!A || !Bp) return;
      var w0 = Math.min(A[0], Bp[0]), w1 = Math.max(A[0], Bp[0]);
      var seed = 0;
      for (var i = 0; i < today.length; i++) seed = (seed * 31 + today.charCodeAt(i)) >>> 0;
      var wi2 = w0 + 1 + (seed % Math.max(1, (w1 - w0 - 1)));
      var di2 = 1 + ((seed >>> 7) % 5);
      if (wi2 < 0 || wi2 >= nWeeks) return;
      var wq = cell(wi2, di2);
      if (fieldAt(wq[0], wq[1]) > COAST) return;
      landmarks.push({ x: wq[0], y: wq[1], key: 'whale', mode: 'afloat', band: 2, afloat: true });
    })();

    /* a milestone at every 1 January, on the coast road (survey-map.js:3910) */
    (function () {
      var years = {};
      for (var w = 0; w < nWeeks; w++) for (var d = 0; d < 7; d++) {
        var dt = weeks[w][d].date;
        if (dt && dt.slice(5) === '01-01') years[dt] = [w, d];
      }
      Object.keys(years).forEach(function (iso) {
        var t0b = X.tOf(iso), mx = alongOf(t0b), best = null, bestGap = Infinity;
        var ru = roadAt(t0b);
        if (ru !== null) best = [mx, acrossOf(ru + 0.05)];
        for (var dw = 0; dw <= 2 && !best; dw += 0.5) {
          for (var side = -1; side <= 1; side += 2) {
            var t = t0b + side * dw;
            for (var b = 0; b < 7; b++) {
              var qx = alongOf(t), qy = acrossOf(b + 0.5);
              var v = fieldAt(qx, qy);
              if (v <= COAST) continue;
              if (v - COAST < bestGap) { bestGap = v - COAST; best = [mx, qy]; }
            }
            if (!dw) break;
          }
        }
        if (best) standLandmark('milestone', best[0], best[1]);
      });
    })();
    function dateIdx(iso) {
      for (var w = 0; w < nWeeks; w++) for (var d = 0; d < 7; d++)
        if (weeks[w][d].date === iso) return [w, d];
      return null;
    }

    /* the settlement runs, for the card (E6) */
    var settles = [];
    var MONTHS_L = ['January','February','March','April','May','June','July',
                    'August','September','October','November','December'];
    var NUM_W = ['', 'one','two','three','four','five','six','seven','eight','nine','ten',
                 'eleven','twelve','thirteen','fourteen','fifteen','sixteen','seventeen',
                 'eighteen','nineteen','twenty','twenty-one','twenty-two','twenty-three',
                 'twenty-four','twenty-five','twenty-six'];
    function numWord(n) { return NUM_W[n] || String(n); }
    function sdate(iso, other) {
      var dt = new Date(iso + 'T00:00:00Z');
      var sameY = other && other.slice(0, 4) === iso.slice(0, 4);
      return dt.getUTCDate() + ' ' + MONTHS_L[dt.getUTCMonth()] + (sameY ? '' : ' ' + dt.getUTCFullYear());
    }

    /* THE SETTLEMENTS COME LAST (survey-map.js:4126). Every mark on
       the sheet has claimed its ground by now — the bench marks at
       3145, then every landmark — and a village takes what is left. */
    (function () {
      var NS = newSeriesCommits(), TOL = 0.75, steady = [], w, d;
      for (w = 0; w < nWeeks; w++) {
        var v = [];
        for (d = 0; d < 7; d++) {
          var dy = weeks[w][d], dow = (d + 6) % 7;
          if (dow >= 5 || !dy.inRange || dy.date >= today) continue;
          v.push(dy.count);
        }
        if (v.length < 5) { steady.push(false); continue; }
        var mn = Infinity, sum = 0;
        v.forEach(function (c) { if (c < mn) mn = c; sum += c; });
        steady.push(mn >= SEA_C && sum / v.length >= NS * TOL);   /* R70: SEA_C */
      }
      var runs2 = [], cur = null;
      for (var i = 0; i < nWeeks; i++) {
        if (steady[i]) { if (cur) cur.b = i; else cur = { a: i, b: i }; }
        else if (cur && steady[i + 1] && i + 1 < nWeeks) { /* bridged */ }
        else if (cur) { runs2.push(cur); cur = null; }
      }
      if (cur) runs2.push(cur);
      function kindOf(n) { return n >= 13 ? 'village' : n >= 12 ? 'hamlet' : n >= 6 ? 'farmstead' : n >= 3 ? 'barn' : null; }
      var chunks = [];
      runs2.forEach(function (r) {
        var a = r.a;
        while (r.b - a + 1 >= 13) { chunks.push({ a: a, b: a + 12 }); a += 13; }
        if (kindOf(r.b - a + 1)) chunks.push({ a: a, b: r.b });
      });
      chunks.forEach(function (ch) {
        var n = ch.b - ch.a + 1, kind = kindOf(n);
        var spec = ART && ART.SETTLEMENTS[kind];
        if (!spec) return;
        var cands = [];
        for (var w2 = ch.a; w2 <= ch.b && w2 < nWeeks; w2++) {
          for (var d2 = 0; d2 < 7; d2++) {
            if ((d2 + 6) % 7 >= 5) continue;
            var c = cell(w2, d2);
            if (fieldAt(c[0], c[1]) <= COAST + 0.4) continue;
            cands.push({ x: c[0], y: c[1], s: slopeAt(c[0], c[1]) });
          }
        }
        if (!cands.length) return;
        cands.sort(function (p, q) { return p.s - q.s; });
        var at = cands[0];
        for (var k = 0; k < cands.length; k++) {
          var bx = { x1: cands[k].x - spec.box[0] / 2, x2: cands[k].x + spec.box[0] / 2,
                     y1: cands[k].y - spec.box[1] / 2, y2: cands[k].y + spec.box[1] / 2 };
          if (!hits(bx)) { at = cands[k]; break; }
        }
        reserve({ x1: at.x - spec.box[0] / 2, x2: at.x + spec.box[0] / 2,
                  y1: at.y - spec.box[1] / 2, y2: at.y + spec.box[1] / 2 });
        /* ST1 (2026-09-30): A SETTLEMENT HAS NO PLAN ON THE FLAT SHEET. It has
           no glyph, so flat it was a box of ink that said nothing — Tommy:
           "you just wonder what these black boxes are with no indication at
           all?", and of the choices (a label, a glyph, or none) "Leave
           settlements off the flat sheet". It comes up with the relief as a
           solid, as every building does; the card still names its days. */
        spec.lay.forEach(function (L) { addBuilding(at.x + L[1], at.y + L[2], L[0], L[3], false, true); });
        /* E6: WHAT THE DAY IS TOLD IT IS PART OF. The sheet wrote the
           settlement's name onto every one of its days' hit rectangles
           (survey-map.js:4215, `settleOf`); the card reads the same
           sentence off the run instead of off 1,800 copies of it. */
        var sFirst = weeks[ch.a][1].date;
        var sLast = weeks[Math.min(ch.b, nWeeks - 1)][5].date;
        settles.push({ from: weeks[ch.a][0].date, to: weeks[Math.min(ch.b, nWeeks - 1)][6].date,
          name: kind.charAt(0).toUpperCase() + kind.slice(1) + ', settled ' +
            sdate(sFirst, sLast) + ' to ' + sdate(sLast) + ' \u00b7 ' + numWord(n) +
            ' weeks of directed days' });
      });
    })();


    /* -------- 8. THE BENCH MARKS and THE CAMP ----------------------
       The chiselled Ordnance bench mark — a horizontal cut with a broad
       arrow beneath it — with its roman numeral beside the cut
       (survey-map.js:4096). And at the front, on today's own cell, the
       camp of the survey party: "what stood there in life". */
    if (!front.none && !front.all) {
      var fw = Math.round((front.e0 - C.px) / C.cw) - baseW;
      var fd = front.turned ? 6 - front.row : front.row;
      var pc = cell(fw, fd);
      reserve(plateBox(pc[0], pc[1], 'camp'));       /* R11 (b): it reserved nothing */
      /* ==== D1: AND THE CAMP IS NOT IN THIS BUFFER ANY MORE. The party
         MOVES — the reader paces it over the seven days of the front —
         and this list is cut once per band when the record lands, so the
         surveyor gets the pinned day's treatment instead (drawDyn below,
         laid by engine.js doomDraw). The ground it stands on is still
         reserved here, so the lettering keeps clear of the front whether
         the party is standing on it this instant or not. ==== */
    }

    /* ==== BI1 (2026-09-29): WHERE A SOLID STANDS, IT IS THE FORM. Tommy:
       "see the 3d buildings on the land in that link, do they have an
       'icon' as well which is 'inside' them?" They did: a landmark that is
       a building printed its glyph AND stood its solid at the same point,
       so on the raised sheet the upright plate poked out of the solid's
       foot, and a holiday's landing vignette set down beside it (the tent
       at Sizewell, inside the dome) did the same. So a sprite whose anchor
       is inside a building's own reach is flagged here and the sprite
       program takes it off as the relief comes up (SPR_BODY, band + 10).
       The flat sheet is what it was: there the building is a footprint and
       the glyph is the symbol. The reach is the kit's parts from the
       building's point, plus the two units the vignette's foot spreads. */
    function underSolid(x, y) {
      for (var i = 0; i < BUILD.length; i++) {
        var b = BUILD[i], kit = ART && ART.BUILDINGS[b.kind];
        if (!kit) continue;
        var r = 0;
        kit.parts.forEach(function (p) {
          r = Math.max(r, Math.sqrt(p.x * p.x + p.y * p.y) + Math.max(p.w, p.d) * 0.5);
        });
        var dx = x - b.x, dy = y - b.y;
        if (dx * dx + dy * dy <= (r + 2) * (r + 2)) return true;
      }
      return false;
    }
    landmarks.forEach(function (L) {
      if (ART && ART.LM_BUILDING[L.key] && underSolid(L.x, L.y)) L.solid = 1;
      /* LH2: the building's reach in map units, for day.js's hover */
      var kit = ART && ART.LM_BUILDING[L.key] && ART.BUILDINGS[ART.LM_BUILDING[L.key]];
      if (kit && !L.afloat) {
        var r = 0;
        kit.parts.forEach(function (p) {
          r = Math.max(r, Math.sqrt(p.x * p.x + p.y * p.y) + Math.max(p.w, p.d) * 0.5);
        });
        L.reach = +r.toFixed(2);
        L.tall = +Math.max.apply(null, kit.parts.map(function (p) { return (p.z || 0) + (p.h || 0); })).toFixed(2);
      }
    });
    plates.forEach(function (P) { if (P.sym === 'vig' && underSolid(P.x, P.y)) P.solid = 1; });

    /* -------- hand it all over ------------------------------------- */
    var fams = {}, bytes = 0;
    FAM.forEach(function (f) {
      var arr = new Float32Array(B[f]);
      fams[f] = arr; bytes += arr.byteLength;
    });
    /* THE BUILDINGS ARE ONE DRAW OF BOXES THAT COLLAPSE TO THEIR OWN
       FOOTPRINTS. reliefOf(0) is 0, so at tilt 0 every part's height is
       nought and what is left on the paper is exactly the black plan form
       the sheet prints (styles.css .sv-footprint); as the sheet is raised
       the same instances stand up into the solids of v24. One draw, both
       forms, and nothing to switch between them.
       Instance: x, y (sheet units), w, d (map units), rot, z0, h, tone. */
    var TONE = { wall: 0, roof: 1, ink: 2, rock: 3 };
    var bp = [];
    BUILD.forEach(function (b) {
      var kit = ART.BUILDINGS[b.kind];
      if (!kit) return;
      var cr = Math.cos(b.rot), sr = Math.sin(b.rot);
      kit.parts.forEach(function (p) {
        /* R37 (b): DOES THIS PART CARRY ANOTHER ON ITS TOP? The flag
           rides in bit 6 of the instance's flags and the shader takes
           BLD_LIFT off the height — see the note at BLD_LIFT. */
        var carries = 0;
        for (var oi = 0; oi < kit.parts.length; oi++) {
          var o2 = kit.parts[oi];
          if (o2 !== p && Math.abs(o2.z - (p.z + p.h)) < 1e-6) { carries = 1; break; }
        }
        var px2 = b.x + p.x * cr - p.y * sr, py2 = b.y + p.x * sr + p.y * cr;
        var round = (p.s === 'cyl' || p.s === 'cone' || p.s === 'dome') ? 1 : 0;
        var taper = (p.s === 'cone' || p.s === 'pyr') ? 1 : (p.s === 'dome' ? 2 : (p.s === 'gable' ? 3 : 0));
        bp.push(px2, py2, p.w, p.d, (p.rot || 0) + b.rot, p.z, p.h,
                TONE[p.tone] + round * 4 + taper * 8 + (b.water ? 32 : 0) + carries * 64 + (b.noPlan ? 128 : 0));
      });
    });
    var bd = new Float32Array(bp);
    bytes += bd.byteLength;
    return {
      fams: fams, stride: STRIDE, bparts: bd, bpStride: 8,
      settles: settles,
      buildings: BUILD.length, landmarks: landmarks, plates: plates,
      occupied: occupied, bytes: bytes,
      counts: (function () { var o = {}; FAM.forEach(function (f) { o[f] = B[f].length / STRIDE; });
        o.buildings = bd.length / 8; o.landmarks = landmarks.length; o.plates = plates.length; return o; })(),
      ms: +((root.performance || Date).now() - t0).toFixed(1)
    };
  }

  /* a separable box blur, three passes — field.js's `smooth`, inlined so
     marks.js does not depend on the order the two scripts load in */
  function blur(src, W, H, rx, ry) {
    var d = new Float32Array(src), row = new Float32Array(Math.max(W, H));
    function cl(v, a, b) { return v < a ? a : v > b ? b : v; }
    for (var pass = 0; pass < 3; pass++) {
      for (var y = 0; y < H; y++) {
        var o = y * W, i, sum = 0;
        for (i = 0; i < W; i++) row[i] = d[o + i];
        for (i = -rx; i <= rx; i++) sum += row[cl(i, 0, W - 1)];
        var inv = 1 / (2 * rx + 1);
        for (var x = 0; x < W; x++) {
          d[o + x] = sum * inv;
          sum += row[cl(x + rx + 1, 0, W - 1)] - row[cl(x - rx, 0, W - 1)];
        }
      }
      for (var x2 = 0; x2 < W; x2++) {
        var i2, s2 = 0;
        for (i2 = 0; i2 < H; i2++) row[i2] = d[i2 * W + x2];
        for (i2 = -ry; i2 <= ry; i2++) s2 += row[cl(i2, 0, H - 1)];
        var inv2 = 1 / (2 * ry + 1);
        for (var y2 = 0; y2 < H; y2++) {
          d[y2 * W + x2] = s2 * inv2;
          s2 += row[cl(y2 + ry + 1, 0, H - 1)] - row[cl(y2 - ry, 0, H - 1)];
        }
      }
    }
    return d;
  }

  /* MARCHING SQUARES, segments only. The SVG traced ordered polylines
     because it had to write one `d`; an instanced draw wants segments,
     and a wall or a scallop is the same picture either way. Strided, so
     the whole 2,316 x 346 lattice is not walked four times. */
  function segmentsAt(d, W, H, lv, lat, stride) {
    var out = [], s = stride || 1;
    function ix(x, y) { return lat.X0 + x * lat.DX; }
    function iy(x, y) { return lat.Y0 + y * lat.DY; }
    for (var y = 0; y + s < H; y += s) {
      for (var x = 0; x + s < W; x += s) {
        var a = d[y * W + x], b = d[y * W + x + s];
        var c = d[(y + s) * W + x + s], e = d[(y + s) * W + x];
        var k = (a > lv ? 1 : 0) | (b > lv ? 2 : 0) | (c > lv ? 4 : 0) | (e > lv ? 8 : 0);
        if (k === 0 || k === 15) continue;
        function lerp(p, q) { return (lv - p) / ((q - p) || 1e-9); }
        var T = [ix(x + lerp(a, b) * s), iy(y)],             // top
            R = [ix(x + s), iy(y + lerp(b, c) * s)],
            Bm = [ix(x + lerp(e, c) * s), iy(y + s)],
            L = [ix(x), iy(y + lerp(a, e) * s)];
        var pairs = null;
        switch (k) {
          case 1: case 14: pairs = [L, T]; break;
          case 2: case 13: pairs = [T, R]; break;
          case 3: case 12: pairs = [L, R]; break;
          case 4: case 11: pairs = [R, Bm]; break;
          case 6: case 9:  pairs = [T, Bm]; break;
          case 7: case 8:  pairs = [L, Bm]; break;
          case 5: out.push([L[0], L[1], T[0], T[1]], [R[0], R[1], Bm[0], Bm[1]]); continue;
          case 10: out.push([T[0], T[1], R[0], R[1]], [L[0], L[1], Bm[0], Bm[1]]); continue;
        }
        if (pairs) out.push([pairs[0][0], pairs[0][1], pairs[1][0], pairs[1][1]]);
      }
    }
    return out;
  }


  /* =================================================================
     DRAWING — the main thread's half.

     Three programs:
       FAM   one instanced draw per land family (six of them), the
             symbol's geometry in MAP units and the pen in DEVICE px;
       BLD   the buildings: a box per part that collapses to its own
             footprint at tilt 0, because reliefOf(0) is 0;
       SPR   the landmark sprites and the bench-mark plates, upright,
             depth-tested, out of one atlas cut at runtime.
     ================================================================= */

  /* ==== R52: AND THE PLATE'S PRINT TAKES THE LANTERN TOO ==============
     R48 warmed the GROUND fragment on the lantern's pool at night — the
     sea, the tints, the contours, the hachure, the scarp's bedding, all
     of which are that one shader's. The marks and the names are their own
     programs drawn OVER it, and they stayed cold: measured over the TERRA
     INCOGNITA band at 23:00, the paper gained 15.6 of r-b and the glyphs'
     ink gained 0.8 (REPORT-room-R48, TODO(R48) in engine.js). On the desk
     the sheet's own printed furniture IS warmed (objects.js runs lampTemp
     over its print), so the plate's print is warmed here by exactly the
     same rule: the same five lines, the same two uniforms off
     RoomObjects.lampSheet(S), the same pool in the same SHEET units —
     ONE lamp on the desk, not six — applied LAST to the colour, before
     the premultiply. uLampN is exactly 0 by day, so the daylight picture
     is the one it was to the bit.

     ONE COPY FOR THE THREE PROGRAMS in this file (the families, the
     buildings, the sprites), spliced into each fragment shader. Six
     copies of the rule now: objects.js (the sheet), day.js (the card),
     engine.js's frame-1 sheet, engine.js's ground, here, and
     lettering.js. ==================== */
  var LAMP_FS = `
uniform vec4 uLampS;    // R52: the lantern in sheet units: x, y, and the pool's two radii
uniform float uLampN;   // and the night, which is nought by day
vec3 lampTemp(vec3 c, vec2 wp){
  if (uLampN <= 0.0) return c;
  vec2 d = (wp - uLampS.xy)/uLampS.zw;
  float pool = 1.0 - clamp((length(d) - 0.20)/0.80, 0.0, 1.0);
  vec3 t = mix(vec3(0.985,0.999,1.022), vec3(1.018,0.998,0.930), pool);
  /* LK1: and the lamp is the light. The paper outside the pool falls into
     shadow at night (to half), and into a dusk dimness before it. */
  float rr = length(d); float lit = 0.40 + 0.58*(1.0 - smoothstep(0.12, 0.95, rr));
  return c*mix(vec3(1.0), t*lit, uLampN);
}
`;
/* ==== FD2 (a), 2026-09-20: THE PAPER THIS INK IS LYING ON ============
   objects.js's own chunk: paperSurf (the sheet's face, which XFORM's
   sheetToClip puts every map point on) and paperInk (the light the sheet
   throws on ink printed on it). The stub is for a harness that loads this
   module without the desk. ==== */
var PAPER_FS = (root.RoomObjects && root.RoomObjects.paperInkGLSL)
  ? root.RoomObjects.paperInkGLSL()
  : 'float paperSurf(vec2 p){ return 0.0; }\nvec3 paperInk(vec3 c, vec2 p){ return c; }\nconst float uPapInk = 1.0;\n';


  /* ---- the family program ----------------------------------------- */
  var FAM_VS_HEAD = `#version 300 es
precision highp float; precision highp sampler2D;
layout(location = 0) in vec2 a;      // the unit quad
layout(location = 1) in vec4 iA;     // x, y (sheet units) and a delta (sheet units)
layout(location = 2) in vec4 iB;     // size, seed, kind, band
out vec2 vUv;
/* ==== R5 (a): THESE ARE PER-INSTANCE, SO THEY ARE flat. They were
   smooth, and a smooth varying is INTERPOLATED PERSPECTIVE-CORRECTLY:
   (v0/w0)a + (v1/w1)b ... over (1/w0)a + (1/w1)b ..., which is exactly
   v only in exact arithmetic. At a grazing tilt the four corners of one
   road segment's quad have w's that differ by a large factor, and the
   division comes back 99.9998 instead of 100.0 — so vKind >= 100.0
   failed and the fragment took the branch BELOW it, the track's node,
   which draws a paper disc with an ink rim. That is the string of white
   pearls along every coast: not a mark sized in world units at all, one
   floating-point comparison one ulp on the wrong side. flat makes the
   value the provoking vertex's, exactly, and is cheaper besides. ==== */
flat out float vSeed; flat out float vKind; flat out float vAl;
flat out float vLen; flat out float vW;
/* R52: the flat sheet point the lantern's pool is measured in — the same
   space engine.js's ground fragment carries, mapToSheet(m,h).xy, height
   excluded, so the pool lies on the paper and does not swim under a pan. */
out vec2 vSheet;
uniform vec2 uVpx;
uniform float uRelief, uDpr;
uniform vec2 uFade;                  // the mid and near generations
`;
  var FAM_VS_BODY = `
vec2 scr(vec4 c){ return vec2((c.x/c.w*0.5+0.5)*uVpx.x, (0.5 - c.y/c.w*0.5)*uVpx.y); }
float groundAt(vec2 m){
  return max(riseOf(fieldAt(m)), 0.0) * mouldMask(m) * EXAGC * uRelief;
}
void main(){
  float band = iB.w;
  float al = band < 0.5 ? 1.0 : (band < 1.5 ? uFade.x : uFade.y);
  al *= reachOf(iA.xy);     /* R13 (b): no mark where no ground is drawn */
  /* ==== TR1 (2026-10-02): AND NO MARK EAST OF THE FRONT. Tommy: "you can
     see those trees going off into the unsurveyed land in that zoom one I
     did." The marks are laid whole weeks at a time, up to and including
     the front's own week (frontT + 1), so in the front column the days
     still to come were planted as well, and their trees stood on Terra
     Incognita's paper beside the survey party. The land is cut at the
     front by surveyedAt (FE1's day-shaped edge) or a day the party has
     paced (D2); a mark now asks the ground's own question at its foot.
     West of the record and north and south of its rows it asks at the
     nearest point of the record, so only the east is cut; a sheet with no
     front set (uFrontK.x 0) is left as it was. ==== */
  if (uFrontK.x > 0.5) {
    vec2 sF = vec2(max(iA.x, uFront.x), clamp(iA.y, uFrontC.x, uFrontC.y));
    al *= max(surveyedAt(sF), paceMask(iA.xy));
  }
  vAl = al; vSeed = iB.y; vKind = iB.z; vUv = a;
  if (al < 0.004) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vLen = 0.0; vW = 1.0; vSheet = vec2(0.0); return; }
  vec2 m0 = iA.xy;
  float h0 = groundAt(m0);
  vec3 sh0 = mapToSheet(m0, h0);
  vec4 c0 = sheetToClip(sh0);
  vSheet = sh0.xy;
  vec2 s0 = scr(c0), p;
  float wpx = abs(iB.x) * uDpr;                  /* THE PEN IS DEVICE PIXELS (E2) */
  vW = wpx;
  /* R5 (b): a NEGATIVE size is one piece of a subdivided run and the
     seed is then its cap mask — 1 no cap at the start, 2 no cap at the
     end — so an interior joint does not overhang and print twice. */
  float capS = (iB.x < 0.0 && mod(iB.y, 2.0) >= 0.5) ? 0.0 : 1.5;
  float capE = (iB.x < 0.0 && mod(floor(iB.y * 0.5), 2.0) >= 0.5) ? 0.0 : 1.5;
  if (iB.z >= 100.0) {
    /* A RUN — a seam, a wall, a road, a track. The quad spans the two
       ends and is a fixed number of DEVICE pixels across, so the line
       foreshortens with the ground but its weight on the paper does
       not. The band is six pen widths wide, which leaves room for a
       casing either side of the line it carries. */
    vec2 m1 = m0 + iA.zw;
    vec3 sh1 = mapToSheet(m1, groundAt(m1));
    vec4 c1 = sheetToClip(sh1);
    /* R52: a run can be long, so the pool is taken ALONG it rather than at
       one end — it is a position on the paper, and a smooth varying is
       interpolated perspective-correctly, which is exactly right for one. */
    vSheet = mix(sh0.xy, sh1.xy, a.x);
    vec2 s1 = scr(c1);
    vec2 d = s1 - s0; float L = length(d);
    d = L > 1e-5 ? d/L : vec2(1.0, 0.0);
    vec2 nn = vec2(-d.y, d.x);
    vLen = L;
    p = s0 + d*(a.x*L + a.x*wpx*capE - (1.0-a.x)*wpx*capS) + nn*(a.y-0.5)*wpx*6.0;
  } else if (iB.z >= 10.0) {
    /* a symbol held at ONE APPARENT SIZE: the track's own node */
    vLen = wpx;
    p = s0 + (a - 0.5) * wpx;
  } else {
    /* a GROUND-SCALED symbol — a tuft, a tree. Its figure is in MAP
       units (the sheet's non-scaling-stroke scales the
       geometry and holds only the stroke), so the quad is the
       projection of a square of the ground and the symbol lies on the
       paper and foreshortens with it. */
    float sz = iB.x * 0.5;
    vec4 cx = sheetToClip(mapToSheet(m0 + vec2(sz, 0.0), h0));
    vec4 cy = sheetToClip(mapToSheet(m0 + vec2(0.0, sz), h0));
    vec2 ex = scr(cx) - s0, ey = scr(cy) - s0;
    vLen = length(ex) * 2.0;
    p = s0 + (a.x-0.5)*2.0*ex + (a.y-0.5)*2.0*ey;
  }
  gl_Position = vec4(vec2(p.x/uVpx.x*2.0 - 1.0, 1.0 - p.y/uVpx.y*2.0)*c0.w,
                     c0.z - 0.00035*c0.w, c0.w);
}`;

  var FAM_FS = `#version 300 es
precision highp float;
` + LAMP_FS + PAPER_FS + `
in vec2 vUv;
flat in float vSeed; flat in float vKind; flat in float vAl;
flat in float vLen; flat in float vW;
in vec2 vSheet;
out vec4 o;
uniform float uFam, uDpr, uPen;
/* R4: the marks come up WITH the ground during the reveal, so they never
   stand on the sea while it rises. 1.0 at every other moment. (uFade is
   already the LOD cross-fade's name in these programs, hence uRise.) */
uniform float uRise;
uniform vec4 uInk0, uInk1, uInk2;   /* rgb + opacity, one per kind slot */
float cov(float d, float hw, float aa){ return 1.0 - smoothstep(hw - aa, hw + aa, d); }
void main(){
  vec3 col = uInk0.rgb; float op = uInk0.a, c = 0.0;
  float aa = 0.75 * uDpr;
  if (vKind >= 100.0) {
    /* the quad is (0..1) along the line and (0..1) across six pen widths */
    float acr = (vUv.y - 0.5) * vW * 6.0;          // device px off the centre line
    float along = vUv.x * (vLen + vW * 3.0) - vW * 1.5;
    float k = vKind;
    if (k == 100.0 && uFam == 4.0) {
      /* THE COAST ROAD: two hairlines, a paper line over an ink one */
      c = cov(abs(acr), 1.3 * uDpr, aa) * 1.0;     // the casing, 2.6 wide
      float inner = cov(abs(acr), 0.7 * uDpr, aa); // the road, 1.4 of paper
      col = mix(uInk0.rgb, uInk1.rgb, inner);
      op = mix(uInk0.a, uInk1.a, inner);
      c = max(c, inner);
    } else if (k == 100.0 && uFam == 5.0) {
      /* THE TRACK: a paper casing under a dashed line, 5 on 3 off */
      c = cov(abs(acr), 1.7 * uDpr, aa);           // the casing, 3.4 of paper
      col = uInk1.rgb; op = uInk1.a;
      float dash = fract(along / (8.0 * uDpr));
      float line = cov(abs(acr), 0.55 * uDpr, aa) * step(dash, 0.625);
      col = mix(uInk1.rgb, uInk0.rgb, line);
      op = mix(uInk1.a, uInk0.a, line);
      c = max(c, line);
    } else if (k == 100.0 && uFam == 3.0) {
      /* THE DRY-STONE WALL: a firm dark line, 8 on 4 off */
      float dash = fract(along / (12.0 * uDpr));
      c = cov(abs(acr), vW * 0.5, aa) * step(dash, 0.667);
    } else if (k == 102.0) {
      /* the furrow, and the ford: both light, the ford dashed 2 on 2 */
      float dash = fract(along / (4.0 * uDpr));
      c = cov(abs(acr), vW * 0.5, aa) * (uFam == 4.0 ? step(dash, 0.5) : 1.0);
      if (uFam == 1.0) { col = uInk2.rgb; op = uInk2.a; }
    } else if (uFam == 1.0 && (k == 100.0 || k == 101.0)) {
      /* R9 (b): THE FIELD BOUNDARY IS A PENCIL DASH, 4 on 3, not an ink
         rule. It was the same solid 0.7 px line of #2e261c at 0.62 that
         the dry-stone wall is drawn in, so the plate's own column seams
         competed with the contours at zoom. The dash's phase restarts at
         each piece of a walked run — the seed carries the cap mask and
         there is no attribute left to hold the distance along — and that
         irregularity is what a pencil does anyway. */
      float dash = fract(along / (7.0*uDpr));
      c = cov(abs(acr), vW*0.5, aa) * step(dash, 0.571);
      if (k == 101.0) op *= 0.85;                // the headland, lighter still
    } else if (k == 103.0 && uFam == 2.0) {
      /* THE WOOD'S SCALLOPED EDGE: the segment bowed to the side the
         wood is NOT, so the edge reads as a row of little bays */
      float t = vUv.x;
      float bow = (vSeed > 0.5 ? 1.0 : -1.0) * 0.56 * vLen * (t - t*t) * 2.0;
      c = cov(abs(acr - bow), vW * 0.5, aa);
    } else {
      c = cov(abs(acr), vW * 0.5, aa);
      if (k == 101.0) op *= 0.806;                 // the headland, .5 against .62
    }
  } else if (vKind >= 10.0) {
    /* THE TRACK'S NODE: a paper disc with an ink rim, r = 2.2 */
    float r = length(vUv - 0.5) * 2.0;
    float rim = cov(abs(r - 0.86), 0.10, 0.06);
    float fill = 1.0 - smoothstep(0.80, 0.92, r);
    col = mix(uInk1.rgb, uInk0.rgb, rim);
    op = mix(uInk1.a, uInk0.a, rim);
    c = max(fill, rim);
  } else if (uFam == 0.0) {
    /* ROUGH PASTURE: the marsh's own tuft, three rules —
       M-1.9 -0.9h3.8   M-1.1 0h2.2   M-1.9 0.9h3.8 — in its own units */
    float fu = 3.8 / max(vLen, 1e-3);              // figure units per device px
    vec2 q = (vUv - 0.5) * 3.8;
    float hw = uPen * uDpr * 0.5 * fu, e = aa * fu;
    c = max(max(cov(abs(q.y + 0.9), hw, e) * step(abs(q.x), 1.9),
                cov(abs(q.y),       hw, e) * step(abs(q.x), 1.1)),
                cov(abs(q.y - 0.9), hw, e) * step(abs(q.x), 1.9));
  } else if (uFam == 2.0) {
    /* THE TREES, at the sheet's own figures (survey-map.js:2846):
       deciduous  M-1.5 0.2 q0 -2 1.5 -2 t1.5 2 q0 1.4 -1.5 1.4 t-1.5 -1.4z
                  + a trunk M0 1.6 v1.4
       conifer    M0 -2.3 l1.5 3.6h-3z  + a trunk M0 1.3 v1.5
       R36: deciduous on the smoothest wooded ground, conifer on the
       rougher half of it. The art runs y = -2.3 .. 3.0
       about the tree's point, so the 6-unit box is offset half a unit. */
    float fu = 6.0 / max(vLen, 1e-3);
    vec2 q = (vUv - 0.5) * 6.0; q.y += 0.5;
    float hw = uPen * uDpr * 0.5 * fu, e = aa * fu;
    if (vKind < 0.5) {
      float r = length(vec2(q.x / 1.5, (q.y + 0.8) / 1.7));
      c = cov(abs(r - 1.0) * 1.5, hw, e);
      c = max(c, cov(abs(q.x), hw, e) * step(1.6, q.y) * step(q.y, 3.0));
    } else {
      float side = (abs(q.x) * 3.6 - (q.y + 2.3) * 1.5) / 3.9;
      c = cov(abs(side), hw, e) * step(-2.3, q.y) * step(q.y, 1.3);
      c = max(c, cov(abs(q.y - 1.3), hw, e) * step(abs(q.x), 1.5));
      c = max(c, cov(abs(q.x), hw, e) * step(1.3, q.y) * step(q.y, 2.8));
    }
  } else {
    float r = length(vUv - 0.5) * 2.0;
    c = 1.0 - smoothstep(0.86, 1.0, r);
  }
  /* R52: the lantern LAST, on the ink the family printed, before the
     premultiply — the paper under it is warmed by the same pool. */
  /* FD2 (a): and the paper it is printed on has creases in it and two
     books under it. On the ONE frame that strikes R58's print raster this
     is the identity and the lantern is left off with it — the sheet
     applies both when it samples the ink. */
  col = paperInk(col, vSheet);
  col = lampTemp(col, vSheet);   /* LB1: in the print too */
  float al = c * op * vAl;
  if (al < 0.004) discard;
  o = vec4(col * al, al) * uRise;
}`;

  /* ---- the buildings ---------------------------------------------- */
  var BLD_VS = `#version 300 es
precision highp float; precision highp sampler2D;
layout(location = 0) in vec3 a;      // the unit box, -0.5..0.5 in x and y, 0..1 in z
layout(location = 1) in vec4 iA;     // x, y (sheet), w, d (map units)
layout(location = 2) in vec4 iB;     // rot, z0, h, tone|round|taper
out float vShade; out vec2 vLocal; out float vTop;
flat out float vRound; flat out float vTone; flat out float vPlan;   /* per instance (R5 a) */
out vec2 vSheet;                                   /* R52: the lantern's pool */
uniform float uRelief, uFade;
/* BI1: which mesh this pass draws — 0 the unit box, 1 the round prism */
uniform float uMesh;
/* R33 (b): the plan's own margin, in MAP units - see the note at off */
uniform float uPad;
/* R37 (b): the footing sunk into the earth, in MAP units, and the clip
   bias, both settable from the query so ONE build shoots the pair. */
uniform float uSink, uBias;
/* R37 (b): the hair a part that carries another stops short of it
   (uLift is the XFORM chunk's aim-lift — this one is the JOINT) */
uniform float uJoint;
`;
  var BLD_BODY = `
void main(){
  /* R13 (b): a SOLID cannot fade, so it is cut whole where the ground
     under its footing is not drawn. */
  if (reachOf(iA.xy) < 0.5) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    vShade = 0.0; vLocal = vec2(0.0); vTop = 0.0;
    vRound = 0.0; vTone = 0.0; vPlan = 0.0; vSheet = vec2(0.0);
    return;
  }
  float flags = iB.w;
  float tone = mod(flags, 4.0);
  float round = mod(floor(flags/4.0), 2.0);
  float taper = mod(floor(flags/8.0), 4.0);
  float water = mod(floor(flags/32.0), 2.0);
  /* R37 (b): bit 6 — this part CARRIES another on its top */
  float carries = mod(floor(flags/64.0), 2.0);
  /* ST1: bit 7 — no plan on the flat sheet (a settlement) */
  float noPlan = mod(floor(flags/128.0), 2.0);
  /* BI1: a round part is drawn by the round prism's pass, the rest by the
     box's; each pass drops the other's instances */
  if (abs(round - uMesh) > 0.5) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    vShade = 0.0; vLocal = vec2(0.0); vTop = 0.0;
    vRound = 0.0; vTone = 0.0; vPlan = 0.0; vSheet = vec2(0.0);
    return;
  }
  vTone = tone; vRound = round; vTop = a.z;
  /* what the PLAN prints: the parts on the ground, and not the rock */
  vPlan = (iB.y > 0.001 || tone > 2.5 || water > 0.5 || noPlan > 0.5) ? 0.0 : 1.0;
  /* THE PART TAPERS as it rises where the sheet's kit says it does: a
     cone and a pyramid to a point, a dome to a hemisphere's shoulder,
     a gable to a ridge. At tilt 0 none of it exists, because relief is
     nought there and the box is its own footprint. */
  float t = a.z;
  float k = 1.0;
  if (taper == 1.0) k = 1.0 - t;
  else if (taper == 2.0) k = sqrt(max(1.0 - t*t, 0.0));
  vec2 span = vec2(iA.z, iA.w) * k;
  /* a gable keeps its span and closes to a ridge across it */
  if (taper == 3.0) span = vec2(iA.z, iA.w * (1.0 - t));
  /* ==== R33 (b): AND THE PLAN'S QUAD IS GROWN BY A MARGIN.
     A coverage worked out INSIDE the polygon can only take ink away: the
     rasteriser hands the fragment shader no fragment at all for the pixel
     whose centre falls outside the footprint, so the half of the edge
     that should have spilled outward had nowhere to go and the first cut
     of this item moved the edge pixel by 9 levels out of 255 (measured,
     room-rev5/R33-b-*-v1-175.png at 1330,440). The quad is grown by
     uPad on every side - two device pixels, worked out on the main
     thread from the portal and the view - and vLocal is scaled so that
     +-0.5 is still the TRUE edge. Ink outside it is nought and the
     fragment is discarded, so the margin costs a rim of killed fragments
     and nothing else - no depth, no colour, no bigger building.

     uPad is already nought at the solid attitudes (the main thread fades
     it out with uSolid), so a RAISED box is the geometry it always was
     and its walls are the ink they were. ==== */
  vec2 grow = span + 2.0 * uPad;
  vec2 off = a.xy * grow;
  vLocal = a.xy * (grow / max(abs(span), vec2(1e-4)));
  float cr = cos(iB.x), sr = sin(iB.x);
  vec2 m = iA.xy + vec2(off.x*cr - off.y*sr, off.x*sr + off.y*cr);
  float bed = riseOf(fieldAt(iA.xy));
  float ground = max(bed, 0.0) * mouldMask(iA.xy) * EXAGC * uRelief;
  /* the part's own height, in MAP units: the kit is drawn in the same
     units the footprint is, and the relief lifts it with the ground */
  float up = ground + (iB.y + (iB.z - carries * uJoint) * t) * uRelief;
  /* ==== R37 (b), candidate 2: THE FOOTING GOES INTO THE EARTH.
     The part's bed is the field read at its CENTRE, and the ground
     slopes across the footprint, so the terrain cuts the walls at a
     grazing angle. A footing carried below the sampled ground puts that
     cut inside the earth, where a cut belongs. Only the parts that
     STAND on the ground (z0 nought) and only the base ring. ==== */
  if (t < 0.01 && iB.y < 0.001) up -= uSink * uRelief;   /* BI1: the base ring alone, now the prism has rings up it */
  vShade = 0.72 + 0.28 * t;
  /* R33 (a): AT TILT 0 THE BOX IS A QUAD AT THE GROUND'S OWN DEPTH, and
     it tests and writes depth against the ground drawn before it — so
     the footprint z-fought the terrain and printed as a mould-eaten
     block whose holes moved with the zoom (Tommy: "a lot of buildings
     render quite whack at certain heights"). The same bias the rose
     uses (SPR, mode 3): a hair toward the eye in clip space. */
  vec3 shf = mapToSheet(m, up);
  /* R52: mapToSheet's xy is the FLAT sheet point, the height excluded, so a
     raised box takes the pool where its footing stands — which is where the
     lamp's light actually falls on the desk. */
  vSheet = shf.xy;
  vec4 cf = sheetToClip(shf);
  gl_Position = vec4(cf.xy, cf.z - uBias * cf.w, cf.w);
}`;
  var BLD_FS = `#version 300 es
precision highp float;
` + LAMP_FS + PAPER_FS + `
in float vShade; in vec2 vLocal; in float vTop;
flat in float vRound; flat in float vTone; flat in float vPlan;
in vec2 vSheet;
out vec4 o;
uniform vec4 uWall, uRoof, uInk, uRock;
uniform float uAl, uSolid;
/* FP1 (2026-09-30): the plan on the flat sheet is an outline in fine ink
   over a light tint, not a black footprint. Tommy picked it (he "quite
   like[s] line") over the black plan and over no plan at all; the ?fp=
   switch went with them. uLineW is device pixels. */
uniform float uLineW;
/* R33 (b): ?bldaa=0 - the stepped edge, so the pair shoots from ONE
   build (R28's rule). One and the margin below it are the shipped plan. */
uniform float uAA;
/* R4: the marks come up WITH the ground during the reveal, so they never
   stand on the sea while it rises. 1.0 at every other moment. (uFade is
   already the LOD cross-fade's name in these programs, hence uRise.) */
uniform float uRise;
/* ==== R33 (b): THE FOOTPRINT'S EDGE IS A COVERAGE, NOT A STEP.
   The plate has no multisample (engine.js, antialias: false) — every
   other edge on this sheet is a coverage worked out in its own fragment
   shader, and the buildings were the one program that never did it, so
   the plan printed a rotated rectangle as a flight of stairs (the 4x
   crop of room-rev5/R33-b-before-v2-175.png).

   vLocal is the unit box, -0.5..0.5 across the footprint, so 1/fwidth
   is exactly the footprint's SPAN in device pixels across that axis and
   the rule below is engine.js's inkOf transposed to a filled region:
   clamp(w/2 - d + 0.5, 0, min(1, w)), the ceiling of min(1, w) being
   what keeps a footprint narrower than a pixel at its TRUE area rather
   than a half-pixel heavier — the same physical ink at 1.25 and 1.75.

   IT IS THE CAP'S RULE AND NOT THE WALL'S. On a side face of the raised
   box one of the two local coordinates is constant, so its derivative is
   exactly nought: the test below finds that face and leaves it alone, or
   every vertical corner of a raised building would have been opened into
   a half-transparent seam. At tilt the walls are the ink they were. ==== */
float inkCov(float d, float f){
  float w = 1.0 / max(f, 1e-6);        /* the span across this axis, device px */
  return clamp(w * (0.5 - d) + 0.5, 0.0, min(1.0, w));
}
void main(){
  /* a round part is a cylinder, not a box: the corners of the unit box
     are cut away, which rounds the prism and makes the FOOTPRINT — what
     the sheet actually prints at tilt 0 — a disc */
  float cov;
  if (vRound > 0.5) {
    /* BI1: the round prism's WALL (vTop runs up it; on a cap it is one
       number and its derivative is nought) is the wall it is — the disc
       rule used to discard the whole of it, which left a raised cylinder
       as its two caps floating one over the other (the Sizewell dome) */
    if (fwidth(vTop) > 1e-5) cov = 1.0;
    else {
      float r = length(vLocal);
      cov = inkCov(r, fwidth(r));      /* the disc's own rim, cut soft */
    }
  } else {
    float fx = fwidth(vLocal.x), fy = fwidth(vLocal.y);
    cov = (fx > 1e-5 && fy > 1e-5)
        ? min(inkCov(abs(vLocal.x), fx), inkCov(abs(vLocal.y), fy))
        : 1.0;                          /* a wall: one derivative is nought */
  }
  cov = mix(1.0, cov, uAA);
  if (cov <= 0.002) discard;
  vec4 c = vTone < 0.5 ? uWall : vTone < 1.5 ? uRoof : vTone < 2.5 ? uInk : uRock;
  /* AT TILT 0 THIS IS A PLAN, NOT A MODEL. The sheet prints the black
     footprint of the parts that stand on the ground and nothing else
     (survey-map.js:894, styles.css .sv-footprint); as the sheet is
     raised the same instances become the toned solid of v24. */
  /* BT1 (2026-09-30): THE SOLID IS OPAQUE BY THE TIME IT HAS A WALL WORTH
     THE NAME. Tommy, at #198,324,82,9,-28: "as you start tilting Sizewell
     is a partial 3D model you can see through?" The plan and the solid
     crossfaded over the whole tilt, which was invisible while the plan was
     black (both ends were near opaque); since FP2 the plan is a 0.14 tint,
     so at tilt 9 the dome was a 40% ghost with its own inside showing. The
     crossfade now completes over the first quarter of the relief (about
     the first ten degrees), and the draw lays the boxes' depth down first
     (BT1 in the draw) so the part-way fade shows the nearest face only. */
  float sol = smoothstep(0.0, 0.25, uSolid);
  /* FP1: the distance to the part's own edge in device pixels, and a
     rule uLineW wide on the inside of it */
  float e;
  if (vRound > 0.5) { float rr = length(vLocal); e = (0.5 - rr) / max(fwidth(rr), 1e-6); }
  else e = min((0.5 - abs(vLocal.x)) / max(fwidth(vLocal.x), 1e-6),
               (0.5 - abs(vLocal.y)) / max(fwidth(vLocal.y), 1e-6));
  float ln = clamp(uLineW - e + 0.5, 0.0, 1.0);
  /* LK6 (audit A15): THE PLAN IS PRINTED, NOT SELECTED. Tommy's FP1 line
     stays; what read as a grey selection box was the flat 14% tint inside
     it, which also let the marsh dashes print straight across the dome.
     A building on an Ordnance sheet is KNOCKED OUT of whatever ground it
     stands on and the plan is hatched: so the inside is the paper, ruled
     with a fine diagonal hatch glued to the footprint (device-px spacing,
     so it neither swims under a pan nor thickens with zoom). */
  float hsx = vLocal.x / max(fwidth(vLocal.x), 1e-6) + vLocal.y / max(fwidth(vLocal.y), 1e-6);
  float hP = uLineW * 2.9;
  float hd = abs(fract(hsx / hP) - 0.5) * hP;
  float ht = clamp(uLineW * 0.45 - hd + 0.5, 0.0, 1.0) * 0.6;
  vec3 planCol = mix(vec3(0.945, 0.906, 0.820), uInk.rgb, max(ln, ht));
  vec3 col = mix(planCol, c.rgb * vShade, sol);
  /* R52: at the plan this IS the plate's print — the black footprint the
     sheet prints — and when the sheet is raised it is a solid standing on
     the same paper under the same lamp. Both take the pool. */
  /* FD2 (a): and the paper it is printed on has creases in it and two
     books under it. On the ONE frame that strikes R58's print raster this
     is the identity and the lantern is left off with it — the sheet
     applies both when it samples the ink. */
  col = paperInk(col, vSheet);
  col = lampTemp(col, vSheet);   /* LB1: in the print too */
  float planA = vPlan;          /* LK6: knocked out: the plan is opaque */
  float a = mix(planA, c.a, sol);
  float al = a * uAl * cov;
  if (al < 0.004) discard;
  o = vec4(col * al, al) * uRise;
}`;

  /* ---- the sprites: landmarks and bench-mark plates ---------------- */
  var SPR_VS_HEAD = `#version 300 es
precision highp float; precision highp sampler2D;
layout(location = 0) in vec2 a;
layout(location = 1) in vec4 iA;    // x, y (sheet units), mode, band
layout(location = 2) in vec4 iQ;    // the quad about the anchor, in DEVICE px
layout(location = 3) in vec4 iUV;   // the cell's rect in the atlas
out vec2 vUv; flat out float vAl;                  /* per instance (R5 a) */
out vec2 vSheet;                                   /* R52: the lantern's pool */
uniform vec2 uVpx;
uniform float uRelief;
uniform vec2 uFade;
`;
  var SPR_BODY = `
void main(){
  /* BI1: band + 10 is a sprite standing where its own building's solid
     stands; as the relief comes up the solid is its form and the plate
     goes. uRelief is exactly 0 at the plan, so the flat sheet is unmoved. */
  float own = step(9.5, iA.w);
  float band = iA.w - 10.0 * own;
  /* band 0 always; 1 the mid generation; 2 the near one; 3 lod-wide,
     which is the near generation's COMPLEMENT — the point symbol gives
     way to the place once the reader is close (survey-map.js:3752) */
  float al = band < 0.5 ? 1.0 : band < 1.5 ? uFade.x : band < 2.5 ? uFade.y : (1.0 - uFade.y);
  al *= 1.0 - own * smoothstep(0.0, 0.2, uRelief);
  al *= reachOf(iA.xy);     /* R13 (b): no mark where no ground is drawn */
  vAl = al;
  if (al < 0.004) { gl_Position = vec4(2.0,2.0,2.0,1.0); vUv = vec2(0.0); vSheet = vec2(0.0); return; }
  /* ==== R37 (b), the note under R38: A MARK ANCHORED ON THE CUT READS
     THE GROUND A HAIR WEST OF IT. The bench-mark plate at the survey
     front is anchored ON the front column's east edge, which after R35 is
     the TOP OF THE SCARP'S FALL: read there, its ground is already part
     of the way down the slope and the plate stands half-way down the
     face (Tommy, #1395,431,2650,53,425 — "the white triangle at the
     scarp's foot"). One unit west is the level ground the mark is on,
     and it is the same hair water.js already takes for the scarp's own
     top (uScarp.x - 1.0). Nought at the plan, where there is no fall. ==== */
  vec2 mA0 = iA.xy;
  /* ==== R40: AND THE CLAMP IS THE CUT'S OWN, NOT THE WHOLE EAST'S.
     R37's min pulled EVERY mark east of the cut back to the top of the
     scarp — which was harmless while everything out there stood on a dead
     plane at the datum, and is not harmless now that the downs have a
     height: the compass rose (ROSE_AT x 2132, five hundred units east of
     the cut) would have been printed at the height of the ground at the
     front while the country under it stood somewhere else entirely. So
     the hair west is taken only by a mark standing ON the fall, which is
     the bench-mark plate and nothing else, and the rose lies on the downs
     it is printed on. ==== */
  if (uScarp.y > 0.0 && mA0.x > uScarp.x - 1.0 && mA0.x < uScarp.x + uScarp.y)
    mA0.x = uScarp.x - 1.0;
  float bed = riseOf(fieldAt(mA0) + terraOf(mA0));
  float mask = mouldMaskT(mA0);
  /* mode 0 stands on its own ground; 1 floats on the water surface at
     the datum; 2 lies on the bed and is seen through the water; 3 LIES
     ON THE GROUND AND IS SIZED IN MAP UNITS (R5 d, the compass rose) */
  float h = (iA.z < 0.5 || iA.z > 2.5) ? max(bed, 0.0) * mask * EXAGC * uRelief
          : iA.z < 1.5 ? 0.0
          : bed * mask * EXAGC * uRelief;
  /* ==== R5 (d): FLAT. Every other sprite is a rigid plate built in
     DEVICE pixels about its anchor's projection, because a landmark is a
     thing standing on the ground and the sheet holds it at one apparent
     size. The rose is not a thing on the ground: it is PRINTED ON THE
     PAPER, so its quad is four corners of the paper itself, in MAP units
     — it scales with the sheet, foreshortens with it, and turns with the
     bearing, which is the whole point of a rose. ==== */
  if (iA.z > 2.5) {
    vec2 m = iA.xy + iQ.xy + a * iQ.zw;
    /* RZ1 (2026-10-02): each corner on ITS OWN ground. The rose is depth
       tested now (drawRose), and the downs under it roll, so a quad held at
       the anchor's height went under the ground where it rose */
    vec2 mc = m;
    if (uScarp.y > 0.0 && mc.x > uScarp.x - 1.0 && mc.x < uScarp.x + uScarp.y) mc.x = uScarp.x - 1.0;
    float hc = max(riseOf(fieldAt(mc) + terraOf(mc)), 0.0) * mouldMaskT(mc) * EXAGC * uRelief;
    vec3 shf = mapToSheet(m, hc);
    /* R52: the rose is PRINTED ON THE PAPER and spans map units, so its
       pool is taken corner by corner rather than at the anchor. */
    vSheet = shf.xy;
    vec4 cf = sheetToClip(shf);
    gl_Position = vec4(cf.xy, cf.z - 0.0009 * cf.w, cf.w);
    vUv = mix(iUV.xy, iUV.zw, a);
    return;
  }
  vec3 sh0 = mapToSheet(iA.xy, h);
  vec4 c0 = sheetToClip(sh0);
  /* R52: a rigid plate is a few device pixels about its anchor, so the
     anchor's own sheet point is the pool for the whole card. */
  vSheet = sh0.xy;
  vec2 s0 = vec2((c0.x/c0.w*0.5+0.5)*uVpx.x, (0.5 - c0.y/c0.w*0.5)*uVpx.y);
  /* UPRIGHT, AND A RIGID PLATE. The card is built in DEVICE pixels about
     the anchor's projection, so a landmark is the same size on screen at
     every zoom and every tilt — which is what scale(1.85/z) buys
     on the sheet — and it stands rather than lying on the ground. */
  vec2 p = s0 + vec2(iQ.x + a.x*iQ.z, iQ.y + a.y*iQ.w);
  gl_Position = vec4(vec2(p.x/uVpx.x*2.0 - 1.0, 1.0 - p.y/uVpx.y*2.0)*c0.w,
                     c0.z - 0.0009*c0.w, c0.w);
  vUv = mix(iUV.xy, iUV.zw, a);
}`;
  var SPR_FS = `#version 300 es
precision highp float; precision highp sampler2D;
` + LAMP_FS + PAPER_FS + `
in vec2 vUv; flat in float vAl;
in vec2 vSheet;
out vec4 o;
uniform sampler2D uAtlas;
/* R4: the marks come up WITH the ground during the reveal, so they never
   stand on the sea while it rises. 1.0 at every other moment. (uFade is
   already the LOD cross-fade's name in these programs, hence uRise.) */
uniform float uRise;
void main(){
  /* ==== BG1 (2): THE ATLASES ARE PREMULTIPLIED. Tommy: the marsh
     symbols "print as solid black bars" zoomed in. Every atlas was
     uploaded STRAIGHT (UNPACK_PREMULTIPLY_ALPHA_WEBGL false) from a
     canvas whose empty texels are (0,0,0,0), so the bilinear filter
     mixed each edge texel's colour toward BLACK before the alpha was
     applied — an ink fringe darker than the ink itself, and the marsh's
     three 0.8-unit rules are nearly all edge. Measured headless: a
     fringe pixel (93,126,131) between the rule (79,133,139) and the water
     (116,146,151). The atlases are uploaded premultiplied now, so the
     filter mixes colour-times-coverage and the colour is taken back out
     AFTER it: the edge is the ink, thinner, never a darker ink. ==== */
  vec4 t = texture(uAtlas, vUv);
  float al = t.a * vAl;
  if (al < 0.004) discard;
  t.rgb /= t.a;
  /* R52: the lantern LAST, on the atlas's own colour before the premultiply */
  /* FD2 (a): and the paper it is printed on has creases in it and two
     books under it. On the ONE frame that strikes R58's print raster this
     is the identity and the lantern is left off with it — the sheet
     applies both when it samples the ink. */
  vec3 sc = paperInk(t.rgb, vSheet);
  sc = lampTemp(sc, vSheet);     /* LB1: in the print too */
  o = vec4(sc * al, al) * uRise;
}`;

  /* the art's own boxes, in ART units, and the CSS scale each is held
     at (styles.css: .sv-lm-body 1.85/z, .sv-mark-body and
     .sv-party-body .95/z, .sv-fixed 1/z) */
  var BOX = {
    lm:   { x0: -15, y0: -14, x1: 15, y1: 9,  k: 1.85 },
    bm:   { x0: -12, y0: -13, x1: 36, y1: 9,  k: 0.95 },
    camp: { x0: -12, y0: -18, x1: 26, y1: 10, k: 0.95 },
    trig: { x0: -8,  y0: -9,  x1: 8,  y1: 7,  k: 1 },
    beacon:{x0: -7,  y0: -11, x1: 7,  y1: 8,  k: 1 },
    marsh:{ x0: -8,  y0: -6,  x1: 8,  y1: 6,  k: 1 },
    vig:  { x0: -8,  y0: -9,  x1: 8,  y1: 7,  k: 0.95 },
    /* D1: the surveyor at work, and the same figure sighting through the
       instrument once the front has been paced — the camp's own box, so
       the party is the same size class as the marks it is putting in */
    camp2:{ x0: -12, y0: -18, x1: 26, y1: 10, k: 0.95 },
    /* D1: the four arrows round the surveyor, a size class of their own:
       smaller than any mark, because they are not on the sheet — they
       are what the hand is doing to it */
    arrN: { x0: -5,  y0: -5,  x1: 5,  y1: 5,  k: 0.95 },
    arrS: { x0: -5,  y0: -5,  x1: 5,  y1: 5,  k: 0.95 },
    arrE: { x0: -5,  y0: -5,  x1: 5,  y1: 5,  k: 0.95 },
    arrW: { x0: -5,  y0: -5,  x1: 5,  y1: 5,  k: 0.95 }
  };
  var BANDS = [2, 3, 4, 6, 8];      // device px per ART unit

  /* R4: THE THREE PROGRAMS, ASKED FOR IN ONE PLACE. The engine issues
     them right after the first frame — while the worker is still building
     the field — and attach() asks for the same three by the same names
     when the marks arrive; the engine memoises, so they compile once and
     the compile is off the critical path. Nothing is built twice. */
  function programs(H) {
    var gl = H.gl, EX = 'const float EXAGC = ' + H.EXAG + '.0;', CH = H.chunks;
    return [
      H.prog(gl, FAM_VS_HEAD + CH.FIELD + CH.FRONT + CH.XFORM + EX + FAM_VS_BODY, FAM_FS, 'mark-fam'),
      H.prog(gl, BLD_VS + CH.FIELD + CH.FRONT + CH.XFORM + EX + BLD_BODY, BLD_FS, 'mark-bld'),
      H.prog(gl, SPR_VS_HEAD + CH.FIELD + CH.FRONT + CH.XFORM + EX + SPR_BODY, SPR_FS, 'mark-spr')
    ];
  }

  function attach(H) {
    var gl = H.gl, S = H.S, ART = root.RoomLmArt;
    /* ==== R52: THE ONE LAMP, HANDED TO WHICHEVER PROGRAM IS BOUND.
       RoomObjects.lampSheet(S) is the single place the lantern's pool is
       worked out (R42) — the point in SHEET units and the two radii of the
       oak's own pool, with the NIGHT as its fifth number — so the plate's
       print is lit by the same lamp as the paper under it and not by a
       second one of its own. One 4f + one 1f per bound program per frame;
       lampSheet is a handful of divides off Sky's published point. ==== */
    function setLamp(p) {
      if (!p.u.uLampS) return;
      var lsh = (root.RoomObjects && root.RoomObjects.lampSheet)
        ? root.RoomObjects.lampSheet(S) : [0, 0, 1, 1, 0];
      gl.uniform4f(p.u.uLampS, lsh[0], lsh[1], lsh[2], lsh[3]);
      gl.uniform1f(p.u.uLampN, lsh[4]);
    }
    var EX = 'const float EXAGC = ' + H.EXAG + '.0;';
    var CH = H.chunks;
    var _P = programs(H), pFam = _P[0], pBld = _P[1], pSpr = _P[2];

    var ROSE_OFF = H.rose === '0';          /* ?rose=0 */
    var quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0,0, 1,0, 0,1, 1,1]), gl.STATIC_DRAW);

    /* the unit box: -0.5..0.5 in x and y, 0..1 up */
    var BV = new Float32Array([
      -0.5,-0.5,0,  0.5,-0.5,0,  0.5,0.5,0,  -0.5,0.5,0,
      -0.5,-0.5,1,  0.5,-0.5,1,  0.5,0.5,1,  -0.5,0.5,1]);
    var BI = new Uint16Array([0,2,1, 0,3,2, 4,5,6, 4,6,7, 0,1,5, 0,5,4,
                              1,2,6, 1,6,5, 2,3,7, 2,7,6, 3,0,4, 3,4,7]);
    var bvb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, bvb);
    gl.bufferData(gl.ARRAY_BUFFER, BV, gl.STATIC_DRAW);
    var bib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, bib);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, BI, gl.STATIC_DRAW);
    /* ==== BI1 (2026-09-29): AND A ROUND PART IS A ROUND PRISM. On the box
       its walls were four flat faces the disc rule threw away entire, so a
       raised cyl, cone or dome showed its caps and nothing between them.
       RN sides round a ring CIRCUMSCRIBING the unit disc (radius
       0.5 / cos(pi / RN)), so the disc the caps cut out at 0.5 lies inside
       the polygon exactly as it lay inside the box, and the plan's
       footprint is the same disc to the pixel. Wound outward and counter-
       clockwise as the box is, for ?bldcull. ==== */
    var RN = 16, RR = 0.5 / Math.cos(Math.PI / RN), RVa = [], RIa = [];
    /* and rings up it, so the dome's taper (sqrt(1 - t^2) in BLD_BODY) is
       a shoulder and not a cone: the box only ever had t = 0 and 1 */
    var RZ = [0, 0.3, 0.55, 0.75, 0.88, 0.96, 1], NZ = RZ.length;
    for (var rz = 0; rz < NZ; rz++) for (var ri = 0; ri < RN; ri++) {
      var ra = (ri + 0.5) * 2 * Math.PI / RN;
      RVa.push(RR * Math.cos(ra), RR * Math.sin(ra), RZ[rz]);
    }
    var RC0 = NZ * RN, RC1 = RC0 + 1, RT = (NZ - 1) * RN;
    RVa.push(0, 0, 0, 0, 0, 1);
    for (ri = 0; ri < RN; ri++) {
      var rj = (ri + 1) % RN;
      for (rz = 0; rz < NZ - 1; rz++) {
        var lo = rz * RN, hi = lo + RN;
        RIa.push(lo + ri, lo + rj, hi + rj, lo + ri, hi + rj, hi + ri);   /* the wall */
      }
      RIa.push(RC1, RT + ri, RT + rj,                   /* the top */
               RC0, rj, ri);                            /* the floor */
    }
    var rvb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, rvb);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(RVa), gl.STATIC_DRAW);
    var rib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, rib);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(RIa), gl.STATIC_DRAW);
    var RIN = RIa.length;

    var vaos = {}, bufs = {}, counts = {}, stats = { atlas: null, buildMs: 0, bytes: 0, draws: 0 };
    /* R33 (b): ?bldaa=0 keeps the stepped edge, for the pair */
    var BLD_AA = (root.location && /(\?|&)bldaa=0(&|$)/.test(root.location.search)) ? 0 : 1;
    /* ==== R37 (b): THE THREE CANDIDATES, EACH ON ITS OWN SWITCH, so the
       isolating pair shoots from ONE build (R28's rule).
         ?bldcull=off|back|front   the faces the raised box shows
         ?bldsink=<map units>      the footing carried below the ground
         ?bldbias=<f>             the R33 (a) clip bias, default 0.0009
       The shipped values are the defaults below. ==== */
    function qnum(k, dflt) {
      var m = root.location && new RegExp('[?&]' + k + '=(-?[0-9.]+)').exec(root.location.search);
      return m ? +m[1] : dflt;
    }
    function qstr(k, dflt) {
      var m = root.location && new RegExp('[?&]' + k + '=([a-z]+)').exec(root.location.search);
      return m ? m[1] : dflt;
    }
    var BLD_CULL = qstr('bldcull', 'off');
    var BLD_SINK = qnum('bldsink', 0);
    var BLD_BIAS = qnum('bldbias', 0.0009);
    /* ==== R37 (b): THE JOINT, AND THE WHOLE FIX. A part that carries
       another stops this many MAP units short of it (a day cell is 40, so
       4/100 of a unit is under a tenth of a device pixel at the fit).
       bHouse is a wall box 0..h AND a gable whose footprint is eight per
       cent wider and whose SOFFIT lies at exactly h — the same plane as
       the wall box's cap, read from the same field sample and carried by
       the same bias. depthFunc is LEQUAL, so at every pixel over the house
       both quads passed and which one printed was decided by the last bit
       of the interpolated depth: the wall's CAP (pale) dealt itself
       through the gable (dark) in horizontal streaks, re-dealt on every
       degree of bearing — Tommy's "buildings that flicker like crazy",
       measured at 2,748 device px of AREA at tilt 25 on the near view and
       10,279 at the close one. The chapel's and the church's spires, the
       tower's and the belfry's cones and the ruined columns' lintel all
       sit on their boxes the same way. `?bldlift=0` is the pair. ==== */
    var BLD_LIFT = qnum('bldlift', 0.04);
    var BLD_COL = /[?&]bldcol=1/.test((root.location && root.location.search) || '');

    function famVao(arr) {
      var vb = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, vb);
      gl.bufferData(gl.ARRAY_BUFFER, arr, gl.STATIC_DRAW);
      var v = gl.createVertexArray(); gl.bindVertexArray(v);
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, vb);
      [[1,4,0],[2,4,4]].forEach(function (d) {
        gl.enableVertexAttribArray(d[0]);
        gl.vertexAttribPointer(d[0], d[1], gl.FLOAT, false, 32, d[2] * 4);
        gl.vertexAttribDivisor(d[0], 1);
      });
      gl.bindVertexArray(null);
      return v;
    }

    /* ---- the atlas: the artwork, cut at runtime -------------------- */
    var atlas = gl.createTexture(), cells = {}, band = 0;
    function cellKey(sym, mode, text) { return sym + '|' + (mode || '') + '|' + (text || ''); }
    function buildAtlas(list, b) {
      var t0 = performance.now();
      var need = [], seen = {};
      list.forEach(function (it) {
        var k = cellKey(it.sym, it.mode, it.text);
        if (seen[k]) return; seen[k] = 1;
        need.push(it);
      });
      var boxes = need.map(function (it) {
        var bx = BOX[it.sym === 'lm' ? 'lm' : it.sym] || BOX.trig;
        return { it: it, bx: bx,
                 w: Math.ceil((bx.x1 - bx.x0) * b), h: Math.ceil((bx.y1 - bx.y0) * b) };
      });
      var maxW = 0; boxes.forEach(function (q) { if (q.w > maxW) maxW = q.w; });
      var perRow = Math.max(1, Math.floor(2048 / maxW));
      var W = 0, H = 0, rows = [], cur = { w: 0, h: 0, items: [] };
      boxes.forEach(function (q) {
        if (cur.items.length >= perRow) { rows.push(cur); cur = { w: 0, h: 0, items: [] }; }
        cur.items.push(q); cur.w += q.w; cur.h = Math.max(cur.h, q.h);
      });
      if (cur.items.length) rows.push(cur);
      rows.forEach(function (r) { W = Math.max(W, r.w); H += r.h; });
      var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
      var g = cv.getContext('2d', { alpha: true });
      g.clearRect(0, 0, W, H);
      var out = {}, y = 0;
      rows.forEach(function (r) {
        var x = 0;
        r.items.forEach(function (q) {
          g.save();
          g.beginPath(); g.rect(x, y, q.w, q.h); g.clip();
          g.translate(x - q.bx.x0 * b, y - q.bx.y0 * b);
          g.scale(b, b);
          g.lineJoin = 'round'; g.lineCap = 'round';
          drawSym(g, q.it);
          g.restore();
          out[cellKey(q.it.sym, q.it.mode, q.it.text)] = {
            u0: x / W, v0: y / H, u1: (x + q.w) / W, v1: (y + q.h) / H,
            /* the quad, in CSS px, about the anchor */
            ox: q.bx.x0 * q.bx.k, oy: q.bx.y0 * q.bx.k,
            w: (q.bx.x1 - q.bx.x0) * q.bx.k, h: (q.bx.y1 - q.bx.y0) * q.bx.k
          };
          x += q.w;
        });
        y += r.h;
      });
      gl.activeTexture(gl.TEXTURE3); /* on the atlases' unit, the field back on 0 after: see lettering.js setBand */
      gl.bindTexture(gl.TEXTURE_2D, atlas);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);   /* BG1 (2): premultiplied, see SPR_FS */
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, cv);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, S.texField);
      stats.atlas = { band: b, w: W, h: H, cells: need.length,
                      buildMs: +(performance.now() - t0).toFixed(2), bytes: W * H * 4 };
      return out;
    }
    /* the non-landmark point symbols, in their own art units — the same
       path data the sheet sets (survey-map.js:3757-3785, 4387) */
    function drawSym(g, it) {
      if (it.sym === 'lm') {
        if (ART) ART.drawLandmark(g, it.key, it.mode, it.angle);
        return;
      }
      g.lineWidth = 1 / (g.getTransform ? g.getTransform().a : 1);
      function stroke(d, col, w, op) {
        g.save(); g.globalAlpha = op === undefined ? 1 : op;
        g.strokeStyle = col; g.lineWidth = w; g.stroke(new Path2D(d)); g.restore();
      }
      function fill(d, col, op) {
        g.save(); g.globalAlpha = op === undefined ? 1 : op;
        g.fillStyle = col; g.fill(new Path2D(d)); g.restore();
      }
      if (it.sym === 'vig') {
        /* the landing vignette is the bare figure — no shadow, no rock,
           no ripple (survey-map.js:3622: LANDMARKS[...].draw(body)) */
        var vlm = ART && ART.LANDMARKS[it.key];
        if (vlm) vlm.draw(g);
      } else if (it.sym === 'trig') {
        stroke('M0 -6.2L5.8 4.2H-5.8Z', '#2e261c', 1);
        fill('M0 0.7m-1.4 0a1.4 1.4 0 1 0 2.8 0a1.4 1.4 0 1 0 -2.8 0', '#2e261c');
      } else if (it.sym === 'beacon') {
        stroke('M0 5.8V-7M-4.4 5.8L0 -1.9L4.4 5.8', '#2e261c', 1);
        fill('M0 -7.7m-1.7 0a1.7 1.7 0 1 0 3.4 0a1.7 1.7 0 1 0 -3.4 0', '#2e261c');
      } else if (it.sym === 'marsh') {
        stroke('M-4 -3.4H4M-6.1 0H6.1M-4 3.4H4', '#3f7f88', 0.8, 0.8);
      } else if (it.sym === 'camp') {
        /* ==== D1: THE CAMP IS A SURVEYOR AT WORK. Tommy: today's ground
           is not levelled "because it is being surveyed" — so what stands
           on the front is not a tent and a flag but the party doing the
           work: a level on its tripod, and the figure beside it. Same
           ink, same box, same size class as the tent it replaces. ==== */
        stroke('M-6 8L0 -9L6 8M0 -9V8', '#96322a', 1.15);      /* the tripod */
        stroke('M-3.6 1.6L3.6 1.6', '#96322a', 0.9, 0.85);     /* its brace */
        fill('M-5 -13.4H5V-9.6H-5Z', '#f7efdd', 0.95);         /* the level */
        stroke('M-5 -13.4H5V-9.6H-5Z', '#96322a', 1.1);
        stroke('M5 -11.5H8.4', '#96322a', 1.1);                /* the barrel */
        stroke('M13 2.4V-2.2M13 2.4L10 8M13 2.4L16 8', '#2e261c', 1.05);
        fill('M13 -4.8m-2.2 0a2.2 2.2 0 1 0 4.4 0a2.2 2.2 0 1 0 -4.4 0', '#2e261c');
        stroke('M13 -1.4L6.4 -6.6', '#2e261c', 1);              /* the arm to it */
      } else if (it.sym === 'camp2') {
        /* D1 (4): and the same figure with an eye to the instrument, the
           barrel run out east along the line of sight */
        stroke('M-6 8L0 -9L6 8M0 -9V8', '#96322a', 1.15);
        stroke('M-3.6 1.6L3.6 1.6', '#96322a', 0.9, 0.85);
        fill('M-4 -13.2H4V-10H-4Z', '#f7efdd', 0.95);
        stroke('M-4 -13.2H4V-10H-4Z', '#96322a', 1.1);
        stroke('M4 -11.6H11.6M-4 -11.6H-6.4', '#96322a', 1.1);
        fill('M11.6 -13.2L15.4 -11.6L11.6 -10Z', '#96322a', 0.9);
        stroke('M-11 3.6V-1.4M-11 3.6L-13.5 8M-11 3.6L-8.2 8', '#2e261c', 1.05);
        fill('M-10.2 -4.4m-2.2 0a2.2 2.2 0 1 0 4.4 0a2.2 2.2 0 1 0 -4.4 0', '#2e261c');
        stroke('M-10.4 -1.2L-6.8 -8.4', '#2e261c', 1);
      } else if (it.sym === 'arrN' || it.sym === 'arrS' ||
                 it.sym === 'arrE' || it.sym === 'arrW') {
        /* D1 (3): the four ways the party can walk, in pencil. A chevron
           and nothing else — no head, no shaft, no label, and no ink at
           all except while the hand is on the surveyor's own ground. */
        var dA = it.sym === 'arrN' ? 'M-3.2 1.4L0 -2.4L3.2 1.4'
               : it.sym === 'arrS' ? 'M-3.2 -1.4L0 2.4L3.2 -1.4'
               : it.sym === 'arrE' ? 'M-1.4 -3.2L2.4 0L-1.4 3.2'
               :                     'M1.4 -3.2L-2.4 0L1.4 3.2';
        stroke(dA, '#f7efdd', 3.2, 0.6);      /* the paper taken back a little */
        stroke(dA, '#6b6156', 1.25, 0.8);
      } else if (it.sym === 'bm') {
        /* the chiselled Ordnance bench mark: a horizontal cut with a
           broad arrow beneath it, and its roman numeral beside the cut */
        var d = 'M-8.5 -9H8.5M-7.5 4L0 -9L7.5 4';
        g.save(); g.lineJoin = 'miter'; g.lineCap = 'butt';
        stroke(d, '#f7efdd', 4.4, 0.75);
        stroke(d, '#96322a', 1.9);
        g.restore();
        if (it.text) {
          g.save();
          g.font = '400 8px "Sorts Mill Goudy LN", "Sorts Mill Goudy", Georgia, serif';
          g.textBaseline = 'alphabetic';
          g.lineWidth = 2.6; g.strokeStyle = '#f1e7d1'; g.lineJoin = 'round';
          g.strokeText(it.text, 10.5, 4);
          g.fillStyle = '#55483a'; g.fillText(it.text, 10.5, 4);
          g.restore();
        }
      }
    }

    var sprBuf = gl.createBuffer(), sprVao = gl.createVertexArray();
    gl.bindVertexArray(sprVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, sprBuf);
    [[1,4,0],[2,4,4],[3,4,8]].forEach(function (d) {
      gl.enableVertexAttribArray(d[0]);
      gl.vertexAttribPointer(d[0], d[1], gl.FLOAT, false, 48, d[2] * 4);
      gl.vertexAttribDivisor(d[0], 1);
    });
    gl.bindVertexArray(null);

    var sprList = [], nLm = 0, nPlate = 0, lastBand = 0, lastDpr = 0;
    function bandFor() {
      var want = 1.85 * S.dpr * (S.cam ? S.cam.k : 1);
      for (var i = 0; i < BANDS.length; i++) if (BANDS[i] >= want) return BANDS[i];
      return BANDS[BANDS.length - 1];
    }
    function layoutSprites(force) {
      var b = bandFor();
      if (!force && b === lastBand && S.dpr === lastDpr) return false;
      if (b !== lastBand) { cells = buildAtlas(sprList, b); lastBand = b; }
      lastDpr = S.dpr;
      var buf = new Float32Array(sprList.length * 12), o = 0;
      sprList.forEach(function (it) {
        var c = cells[cellKey(it.sym, it.mode, it.text)];
        if (!c) return;
        buf[o] = it.x; buf[o+1] = it.y; buf[o+2] = it.modeN; buf[o+3] = it.band;
        buf[o+4] = c.ox * S.dpr; buf[o+5] = c.oy * S.dpr;
        buf[o+6] = c.w * S.dpr;  buf[o+7] = c.h * S.dpr;
        buf[o+8] = c.u0; buf[o+9] = c.v0; buf[o+10] = c.u1; buf[o+11] = c.v1;
        o += 12;
      });
      gl.bindBuffer(gl.ARRAY_BUFFER, sprBuf);
      gl.bufferData(gl.ARRAY_BUFFER, buf.subarray(0, o), gl.DYNAMIC_DRAW);
      stats.sprites = o / 12;
      return true;
    }

    function setMarks(M) {
      var t0 = performance.now();
      stats.bytes = M.bytes;
      /* R33 (c): the parts, published for the sweep. Eight floats each,
         the array the VAO is filled from — no copy, no second pass. */
      stats.bparts = M.bparts; stats.bpStride = M.bpStride;
      FAM.forEach(function (f) {
        var arr = M.fams[f];
        counts[f] = arr.length / M.stride;
        if (!counts[f]) return;
        vaos[f] = famVao(arr);
        var bits = 0, kh = {};
        for (var i = 7; i < arr.length; i += M.stride) bits |= 1 << (arr[i] | 0);
        /* the histogram of kinds, in `Room.marksStats().kinds`: it is what
           settled R5 (a) — the pearls could be pinned on a kind the family
           does not contain, which is what named the fault as a varying and
           not a symbol. One pass over a buffer already in cache. */
        for (var j = 6; j < arr.length; j += M.stride) kh[arr[j]] = (kh[arr[j]] || 0) + 1;
        bandsOf[f] = bits;
        stats.kinds = stats.kinds || {}; stats.kinds[f] = kh;
      });
      counts.buildings = M.bparts.length / M.bpStride;
      if (counts.buildings) {
        var bb = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, bb);
        gl.bufferData(gl.ARRAY_BUFFER, M.bparts, gl.STATIC_DRAW);
        /* BI1: two VAOs over the ONE instance buffer — the box, and the
           round prism */
        var bldVao = function (vb, ib) {
          var v = gl.createVertexArray(); gl.bindVertexArray(v);
          gl.bindBuffer(gl.ARRAY_BUFFER, vb);
          gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
          gl.bindBuffer(gl.ARRAY_BUFFER, bb);
          [[1,4,0],[2,4,4]].forEach(function (d) {
            gl.enableVertexAttribArray(d[0]);
            gl.vertexAttribPointer(d[0], d[1], gl.FLOAT, false, 32, d[2] * 4);
            gl.vertexAttribDivisor(d[0], 1);
          });
          gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
          gl.bindVertexArray(null);
          return v;
        };
        vaos.buildings = bldVao(bvb, bib);
        vaos.bround = bldVao(rvb, rib);
      }
      /* the sprite list: the landmarks first, then the plates */
      sprList = [];
      (M.landmarks || []).forEach(function (L) {
        sprList.push({ x: L.x, y: L.y, sym: 'lm', key: L.key, mode: L.mode, angle: L.angle,
                       modeN: L.mode === 'afloat' ? 1 : L.mode === 'bed' ? 2 : 0,
                       band: L.band + (L.solid ? 10 : 0), text: L.key + (L.mode || '') });   /* BI1 */
      });
      nLm = sprList.length;
      (M.plates || []).forEach(function (P) {
        sprList.push({ x: P.x, y: P.y, sym: P.sym, key: P.key, text: P.text || '',
                       modeN: P.sym === 'vig' ? 1 : 0, band: P.band + (P.solid ? 10 : 0) });   /* BI1 */
      });
      nPlate = sprList.length - nLm;
      layoutSprites(true);
      stats.buildMs = +(performance.now() - t0).toFixed(2);
      stats.counts = counts;
    }

    /* the ink each family is set in (styles.css :2115-2133) */
    var INK = {
      rough:  [[COL.water, 0.5], [COL.water, 0.5], [COL.water, 0.5]],
      fields: [[COL.ink2, 0.46], [COL.ink, 0.62], [COL.sepia, 0.4]],  /* R9 (b): a pencil, not ink */
      woods:  [[COL.ink2, 0.7], [COL.ink2, 0.7], [COL.ink2, 0.7]],
      walls:  [[COL.ink, 0.72], [COL.ink, 0.72], [COL.ink, 0.72]],
      roads:  [[COL.ink2, 0.8], [COL.paperHi, 1], [COL.ink, 1]],
      tracks: [[COL.ink2, 1], [COL.paper, 1], [COL.ink2, 1]]
    };
    /* the pen a ground-scaled symbol is drawn with, in CSS px */
    var PEN = { rough: 0.55, fields: 0.7, woods: 0.55, walls: 1.3, roads: 2.6, tracks: 0.9 };
    function u4(p, n, c) { gl.uniform4f(p.u[n], c[0][0], c[0][1], c[0][2], c[1]); }

    /* which bands a family actually has instances in, so a family whose
       whole band is down costs nothing at all */
    var bandsOf = {};
    function famUp(name, f) {
      var b = bandsOf[name];
      if (b === undefined) return true;
      return (b & 1) || ((b & 2) && f[0] > 0.004) || ((b & 4) && f[1] > 0.004);
    }
    function fade() {
      var lz = Math.log(H.fitWidth() / S.view.w) / Math.LN2;
      return S.levForce || [H.smoothstep(H.LEV_B - 0.5, H.LEV_B + 0.5, lz),
                            H.smoothstep(H.LEV_C - 0.5, H.LEV_C + 0.5, lz)];
    }

    function draw() {
      var f = fade(), n = 0;
      var only = H.only || '';
      /* `?marks=` is the review lever: 'lm' the sprites alone, 'fam' the
         families and the buildings, 'fam:<name>' ONE family, 'bld' the
         buildings alone. It exists so a fault can be pinned on a layer
         from a screenshot instead of a guess (R5). */
      /* R9: `fam:` with NO name now draws NO family — the review lever the
         briefs have been citing as "the picture without it". It was
         `/^fam:(.+)$/`, so an empty name fell through to "draw everything"
         and every isolation shot taken with it was the full picture. */
      var famOnly = /^fam:(.*)$/.exec(only);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      /* ==== R2 (a): A SYMBOL IS DRAWN ON THE GLASS, SO IT DOES NOT CUT THE
         WORLD. Every family here is a blended sprite standing at its own
         anchor's projection, and it was WRITING DEPTH — harmless while the
         lettering ran with the depth test off, but the moment a name is
         tested against the buffer (lettering.js draw) a camp on a hillock
         started overprinting the fjord's name behind it, which is not the
         ordering the sheet has. The buffer the letters are tested against
         is the SOLID world — the plate, the water, E7's cut face and the
         buildings, which keep their own depth below — and nothing else.
         The symbols still TEST, so a mark behind a hill is still hidden. ==== */
      gl.depthMask(false);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, S.texField);

      /* 1-6: ONE INSTANCED DRAW PER LAND FAMILY */
      if (only !== 'lm' && only !== 'bld') {
      gl.useProgram(pFam);
      gl.uniform1i(pFam.u.uField, 0);
      H.setCommon(gl, pFam); setLamp(pFam);
      if (pFam.u.uRise) gl.uniform1f(pFam.u.uRise, S.markFade === undefined ? 1 : S.markFade);
      gl.uniform2f(pFam.u.uVpx, S.cv.width / (S.capK || 1), S.cv.height / (S.capK || 1));   /* PK1 */
      gl.uniform1f(pFam.u.uDpr, S.dpr);
      gl.uniform2f(pFam.u.uFade, f[0], f[1]);
      FAM.forEach(function (name, i) {
        if (!counts[name] || !famUp(name, f)) return;
        if (famOnly && famOnly[1] !== name) return;
        gl.bindVertexArray(vaos[name]);
        gl.uniform1f(pFam.u.uFam, i);
        gl.uniform1f(pFam.u.uPen, PEN[name]);
        u4(pFam, 'uInk0', INK[name][0]); u4(pFam, 'uInk1', INK[name][1]); u4(pFam, 'uInk2', INK[name][2]);
        gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, counts[name]);
        n++;
      });
      }

      /* 7: THE BUILDINGS — footprints at tilt 0, solids when raised.
         These ARE solid: a box has its own faces to sort, so it writes. */
      if (counts.buildings && only !== 'lm' && !famOnly && f[0] > 0.004) {
        gl.depthMask(true);
        gl.useProgram(pBld);
        gl.bindVertexArray(vaos.buildings);
        gl.uniform1i(pBld.u.uField, 0);
        H.setCommon(gl, pBld); setLamp(pBld);
        if (pBld.u.uRise) gl.uniform1f(pBld.u.uRise, S.markFade === undefined ? 1 : S.markFade);
        var solid = H.reliefOf ? H.reliefOf(S.tilt) : 0;
        /* FP1: THE PLAN COMES WITH ITS GLYPH. Tommy, on the outline: "it's quite
           mysterious on the flat map, you just wonder what these black boxes
           are with no indication at all?" The plan was lod-mid (f[0]) and
           the glyph that says what it is stands in the NEAR generation
           (band 2, f[1]), so between the two a box was printed with its
           symbol a ghost on it. Flat, the plan now fades with the near
           generation; raised, the solid keeps lod-mid as it was. */
        gl.uniform1f(pBld.u.uAl, f[0] * (f[1] + (1 - f[1]) * solid));
        gl.uniform1f(pBld.u.uSolid, solid);
        /* R33 (b): two device pixels of margin, in MAP units. The plate
           carries S.portal.w css pixels of S.view.w map units, so the
           scale is one division; the pitch of the edge pose varies it
           across the page by under a quarter, and a margin WIDER than
           the pixel it antialiases costs nothing (the ink out there is
           nought and the fragment is discarded). Nought when the box is
           a solid, so the raised building is the geometry it was. */
        var aa = BLD_AA;
        gl.uniform1f(pBld.u.uAA, aa);
        if (pBld.u.uLineW) gl.uniform1f(pBld.u.uLineW, 1.1 * (S.dpr || 1));
        var Pm = S.portalScr || S.portal;       /* MP2 */
        gl.uniform1f(pBld.u.uPad, Pm.w ?
          aa * 2.0 * S.view.w / (Pm.w * (S.dpr || 1)) * (1 - solid) : 0);
        /* R37 (b): the footing, and the bias, both on their switches.
           The sink is in MAP units and rides with the relief, so at the
           plan (relief nought) it is nought and the footprint is the
           footprint. */
        gl.uniform1f(pBld.u.uSink, BLD_SINK);
        gl.uniform1f(pBld.u.uJoint, BLD_LIFT);
        gl.uniform1f(pBld.u.uBias, BLD_BIAS);
        if (BLD_COL) {
          /* the tone diagnostic: wall RED, roof GREEN, rock MAGENTA, and
             the plan's ink taken to black and uSolid to one so nothing
             the mix does can be mistaken for a tone. */
          gl.uniform1f(pBld.u.uSolid, 1);
          gl.uniform4f(pBld.u.uWall, 1, 0, 0, 1);
          gl.uniform4f(pBld.u.uRoof, 0, 0.85, 0, 1);
          gl.uniform4f(pBld.u.uInk, 0, 0, 0, 1);
          gl.uniform4f(pBld.u.uRock, 1, 0, 1, 1);
        } else {
        gl.uniform4f(pBld.u.uWall, 0.90, 0.86, 0.78, 1);
        gl.uniform4f(pBld.u.uRoof, 0.62, 0.52, 0.42, 1);
        gl.uniform4f(pBld.u.uInk, COL.ink[0], COL.ink[1], COL.ink[2], 0.9);
        gl.uniform4f(pBld.u.uRock, 0.55, 0.50, 0.44, 1);
        }
        /* ==== R37 (b): BACK-FACE CULLING — MEASURED, AND NOT SHIPPED.
           There is no culling anywhere in the engine (R28) and a closed
           box is the one place it would be exact, so it was tried as the
           cure for the flicker and measured against it: at tilt 25 it took
           the AREA change from 10,279 device px to 3,241, against 3,154
           for the joint above — the same answer for more. But at TILT 10
           it OPENS A HAIRLINE along the north eaves: the gable's soffit is
           the face that inks the overhang between the wall's head and the
           eave, it is back-facing from above, and culled it leaves a
           one-pixel line of ground showing through (room-rev6/R37-z-back-
           t10.png against -off-, measured 2,430 AREA against 1,575). The
           joint costs nothing and opens nothing, so the cull is a switch
           and not the picture: `?bldcull=back` (or `front`) is the pair.
           It is faded in with the solid either way, because at the plan
           the box is flat and its cap and floor are coplanar, and LEQUAL
           inking the rim twice there is the plan's ink as R33 (b) tuned
           it and as Tommy has it at the fit. ==== */
        var culling = BLD_CULL !== 'off' && solid > 0.001;
        if (culling) {
          gl.enable(gl.CULL_FACE);
          gl.cullFace(BLD_CULL === 'front' ? gl.FRONT : gl.BACK);
        }
        var drawBoxes = function () {
          gl.bindVertexArray(vaos.buildings);
          if (pBld.u.uMesh) gl.uniform1f(pBld.u.uMesh, 0);
          gl.drawElementsInstanced(gl.TRIANGLES, 36, gl.UNSIGNED_SHORT, 0, counts.buildings);
          n++;
          /* BI1: and the round parts, on their own prism */
          if (pBld.u.uMesh && vaos.bround) {
            gl.bindVertexArray(vaos.bround);
            gl.uniform1f(pBld.u.uMesh, 1);
            gl.drawElementsInstanced(gl.TRIANGLES, RIN, gl.UNSIGNED_SHORT, 0, counts.buildings);
            n++;
          }
        };
        /* BT1: A RAISED BOX IS SEEN FROM OUTSIDE ONLY. While the solid is
           fading in (and while the LOD fades it) it is drawn with alpha,
           and a blended box painted its back faces and the inside of the
           dome's fan wherever they came first. So its depth goes down
           first with no colour, and the colour pass (LEQUAL, the same
           program and uniforms, so the same depths to the bit) is kept
           only on the nearest face. At the plan the box is flat and this
           is skipped: the plan is the one draw it was. */
        if (solid > 0.001) {
          gl.colorMask(false, false, false, false);
          drawBoxes();
          gl.colorMask(true, true, true, true);
        }
        drawBoxes();
        if (culling) gl.disable(gl.CULL_FACE);
        gl.depthMask(false);
        n++;
      }

      /* 8-9: the landmark sprites, then the bench-mark plates */
      if (sprList.length && only !== 'fam' && !famOnly && only !== 'bld') {
        layoutSprites(false);
        gl.useProgram(pSpr);
        gl.bindVertexArray(sprVao);
        gl.uniform1i(pSpr.u.uField, 0);
        gl.activeTexture(gl.TEXTURE3); gl.bindTexture(gl.TEXTURE_2D, atlas);
        gl.uniform1i(pSpr.u.uAtlas, 3);
        gl.activeTexture(gl.TEXTURE0);
        H.setCommon(gl, pSpr); setLamp(pSpr);
        if (pSpr.u.uRise) gl.uniform1f(pSpr.u.uRise, S.markFade === undefined ? 1 : S.markFade);
        gl.uniform2f(pSpr.u.uVpx, S.cv.width / (S.capK || 1), S.cv.height / (S.capK || 1));   /* PK1 */
        gl.uniform2f(pSpr.u.uFade, f[0], f[1]);
        if (nLm && f[1] > 0.004) { gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, nLm); n++; }
        if (nPlate) {
          /* WebGL 2 has no base instance, so the plates are their own
             range: a second VAO over the SAME buffer, offset past the
             landmarks. One buffer, two draws, nothing copied. */
          gl.bindVertexArray(plateVao);
          gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, nPlate); n++;
        }
      }
      gl.depthMask(true);
      /* 10: THE ROSE, on the ground it is printed on (R5 d; RZ1: hidden by
         what stands in front of it) */
      if (only !== 'fam' && !famOnly && roseVao && !ROSE_OFF) { drawRose(); n++; }
      gl.disable(gl.BLEND);
      gl.bindVertexArray(null);
      stats.draws = n;
    }

    /* ==== R5 (d): THE ROSE, CUT ONCE AND LAID ON THE PAPER ============
       Where it stands: the v32 overlay printed it at screen (1692.27,
       254.73) with the sheet's own origin at (49, 51.890625) and the
       sheet 0.770727 of the screen, so on the paper it is at
       ((1692.27 - 49) / 0.770727, (254.73 - 51.890625) / 0.770727) =
       (2132.1, 263.2) sheet units — inside the neat line, in the north-
       east where the record has not reached. Its art is in overlay px,
       which the same 0.770727 turns into sheet units.

       Its own texture, not the sprite atlas: the atlas is re-cut at a
       band chosen for a 30-unit landmark, and a 106-unit rose cut at the
       near band would take 824 x 900 px of it — more than the whole
       atlas. One 424 x 454 canvas, cut once at attach, 770 KB on the GPU
       and NOTHING on the wire, serves every zoom to five times the fit.

       DEPTH OFF. The rose is ink on the paper and the paper is under the
       ground; nothing surveyed is ever hidden (STATE), and neither is
       this. It is drawn last, over everything, at the ground's own
       height under its anchor so it sits on the country rather than
       floating at sea level. ==== */
    var ROSE_K = 1 / 0.770727;              // overlay px -> sheet units
    var ROSE_AT = [2132.10, 263.20];        // its place on the paper
    var ROSE_PX = 4;                        // device px per overlay unit in the cut
    var roseTex = null, roseVao = null, roseBytes = 0;
    function cutRose() {
      var R = ART && ART.ROSE;
      if (!R) return;
      var w = Math.ceil((R.x1 - R.x0) * ROSE_PX), h = Math.ceil((R.y1 - R.y0) * ROSE_PX);
      var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      var g = cv.getContext('2d', { alpha: true });
      g.translate(-R.x0 * ROSE_PX, -R.y0 * ROSE_PX);
      g.scale(ROSE_PX, ROSE_PX);
      g.lineCap = 'round';
      R.draw(g);
      roseTex = gl.createTexture();
      gl.activeTexture(gl.TEXTURE3);
      gl.bindTexture(gl.TEXTURE_2D, roseTex);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);   /* BG1 (2): premultiplied, see SPR_FS */
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, cv);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, S.texField);
      roseBytes = w * h * 4;
      stats.rose = { w: w, h: h, bytes: roseBytes };
      /* one instance: mode 3, the quad in MAP units about the anchor */
      var inst = new Float32Array([
        ROSE_AT[0], ROSE_AT[1], 3, 0,
        R.x0 * ROSE_K, R.y0 * ROSE_K, (R.x1 - R.x0) * ROSE_K, (R.y1 - R.y0) * ROSE_K,
        0, 0, 1, 1]);
      var b = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, b);
      gl.bufferData(gl.ARRAY_BUFFER, inst, gl.STATIC_DRAW);
      roseVao = gl.createVertexArray();
      gl.bindVertexArray(roseVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, b);
      [[1,4,0],[2,4,4],[3,4,8]].forEach(function (d) {
        gl.enableVertexAttribArray(d[0]);
        gl.vertexAttribPointer(d[0], d[1], gl.FLOAT, false, 48, d[2] * 4);
        gl.vertexAttribDivisor(d[0], 1);
      });
      gl.bindVertexArray(null);
    }
    function drawRose() {
      if (!roseVao || ROSE_OFF) return;
      gl.useProgram(pSpr);
      gl.bindVertexArray(roseVao);
      gl.uniform1i(pSpr.u.uField, 0);
      gl.activeTexture(gl.TEXTURE3); gl.bindTexture(gl.TEXTURE_2D, roseTex);
      gl.uniform1i(pSpr.u.uAtlas, 3);
      gl.activeTexture(gl.TEXTURE0);
      H.setCommon(gl, pSpr); setLamp(pSpr);
      gl.uniform2f(pSpr.u.uVpx, S.cv.width / (S.capK || 1), S.cv.height / (S.capK || 1));   /* PK1 */
      gl.uniform2f(pSpr.u.uFade, 1, 1);
      /* RZ1 (2026-10-02): R5 (d) drew the rose with the depth test off,
         "over everything and never hidden", when it lay on a dead plane at
         the datum and nothing could stand in front of it. Since R40 the
         downs have height, and a ridge between the eye and the rose was
         drawn through (Tommy, #1498,298,1018,53,-73: "the compass for
         Terra Incognita shows through"). So it is tested against the
         ground like every other mark (its own 0.0009 lift keeps it off the
         ground it lies on), and writes no depth of its own. */
      gl.depthMask(false);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, 1);
      gl.depthMask(true);
    }

    /* the plates are the same buffer read from the landmarks' end */
    var plateVao = gl.createVertexArray();
    function wirePlates() {
      gl.bindVertexArray(plateVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, sprBuf);
      [[1,4,0],[2,4,4],[3,4,8]].forEach(function (d) {
        gl.enableVertexAttribArray(d[0]);
        gl.vertexAttribPointer(d[0], d[1], gl.FLOAT, false, 48, nLm * 48 + d[2] * 4);
        gl.vertexAttribDivisor(d[0], 1);
      });
      gl.bindVertexArray(null);
    }

    /* ==== D1: A SPRITE THAT MOVES GETS ITS OWN SMALL TEXTURE ==========
       The atlas is cut once per BAND for a list that is fixed when the
       record lands, so a sprite whose anchor moves cannot live in that
       buffer. R24 cut the pinned day's marking pin that way first; the
       pin went with the lock (N2) and the surveyor and the arrows round
       it are what is left — they move too, and one of them is not even
       on the sheet until the hand is over it. Each sym gets its own
       texture, cut at the finest band the atlas ever uses, the first
       time it is asked for: nothing at all on the GPU until the party is
       drawn, nothing on the wire ever, and the ground it stands on lifts
       it because the sprite program reads the field. ==== */
    var DYN_BAND = 8;
    var dynS = {};
    function cutDyn(sym) {
      var bx = BOX[sym];
      if (!bx) return null;
      var w = Math.ceil((bx.x1 - bx.x0) * DYN_BAND), h = Math.ceil((bx.y1 - bx.y0) * DYN_BAND);
      var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      var g = cv.getContext('2d', { alpha: true });
      g.translate(-bx.x0 * DYN_BAND, -bx.y0 * DYN_BAND);
      g.scale(DYN_BAND, DYN_BAND);
      g.lineJoin = 'round'; g.lineCap = 'round';
      drawSym(g, { sym: sym });
      var tex = gl.createTexture();
      gl.activeTexture(gl.TEXTURE3);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);   /* BG1 (2): premultiplied, see SPR_FS */
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, cv);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, S.texField);
      var buf = gl.createBuffer(), vao = gl.createVertexArray();
      gl.bindVertexArray(vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      [[1,4,0],[2,4,4],[3,4,8]].forEach(function (d) {
        gl.enableVertexAttribArray(d[0]);
        gl.vertexAttribPointer(d[0], d[1], gl.FLOAT, false, 48, d[2] * 4);
        gl.vertexAttribDivisor(d[0], 1);
      });
      gl.bindVertexArray(null);
      stats[sym] = { w: w, h: h, bytes: w * h * 4 };
      dynS[sym] = { tex: tex, buf: buf, vao: vao, key: '', bx: bx };
      return dynS[sym];
    }
    /* REVIEW 2026-09-19: `flat`, when given, is MAP UNITS PER ART UNIT and
       the sprite is PRINTED ON THE PAPER the way the rose is (mode 3):
       its quad spans map units about the anchor, so it scales with the
       sheet, foreshortens with it and TURNS WITH THE BEARING. The pacing
       chevrons need that — they point along the paper's own north and
       east, and as upright plates they kept pointing up the screen
       (Tommy: "the arrow directions don't rotate when you rotate the map
       so if you rotate 90 degrees they point the wrong way"). Without
       `flat` a sprite is the rigid upright plate it always was. */
    function drawDyn(sym, mx, my, flat) {
      var o = dynS[sym] || cutDyn(sym);
      if (!o) return;
      drawObj(o, mx, my, flat);
    }
    /* ==== PH1 (2026-09-25): A PLATE PAINTED ON DEMAND. The pit's handles
       stand up like the surveyor they belong to — rigid plates a fixed
       size on the glass — but what they show depends on the view (which
       way a step lies on the screen, which way the line of sight runs),
       so they cannot be cut once from the symbol table. A SLOT is one
       texture; `art` is a string naming what is painted in it, and the
       slot is repainted only when that string changes. `bx` is the box in
       art units and its k the css px an art unit stands for, as BOX's are.
       Tommy: "i also still find the alignment handles very tricky to see
       and use" and, of the tilted sheet, "a mess in 3D". ==== */
    /* AL1: `flat`, when given, is what drawDyn's is — map units per art
       unit — and the plate is PRINTED ON THE PAPER instead of standing up:
       the pit's crossing is lettered on the sheet like the sheet's own
       names. bx.k is then only the texture's resolution, the css px an art
       unit is expected to measure on the glass. */
    /* PA2: `off`, when given, is [x, y] css px the plate is carried on the
       glass from its anchor — a plate that belongs at a figure's height but
       beside it (the pit's chevrons) is anchored ON the figure and moved
       out on the screen, so it never stands on lower ground than he does. */
    /* AL5: `top`, when true, draws the plate over the ground whatever the
       ground's depth — a LABEL (the pit's crossing dates), not a thing
       standing in the scene. */
    function drawArt(slot, art, bx, paint, mx, my, flat, off, top) {
      var o = dynS[slot];
      var band = Math.max(2, Math.ceil(S.dpr * bx.k));
      if (!o || o.band !== band) {
        if (o) { gl.deleteTexture(o.tex); gl.deleteBuffer(o.buf); gl.deleteVertexArray(o.vao); }
        var tex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        var buf = gl.createBuffer(), vao = gl.createVertexArray();
        gl.bindVertexArray(vao);
        gl.bindBuffer(gl.ARRAY_BUFFER, quad);
        gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        [[1,4,0],[2,4,4],[3,4,8]].forEach(function (d) {
          gl.enableVertexAttribArray(d[0]);
          gl.vertexAttribPointer(d[0], d[1], gl.FLOAT, false, 48, d[2] * 4);
          gl.vertexAttribDivisor(d[0], 1);
        });
        gl.bindVertexArray(null);
        var cv = document.createElement('canvas');
        cv.width = Math.ceil((bx.x1 - bx.x0) * band); cv.height = Math.ceil((bx.y1 - bx.y0) * band);
        o = dynS[slot] = { tex: tex, buf: buf, vao: vao, key: '', bx: bx, band: band, art: null, cv: cv };
      }
      if (o.art !== art) {
        var g = o.cv.getContext('2d', { alpha: true });
        g.setTransform(1, 0, 0, 1, 0, 0);
        g.clearRect(0, 0, o.cv.width, o.cv.height);
        g.translate(-bx.x0 * band, -bx.y0 * band);
        g.scale(band, band);
        g.lineJoin = 'round'; g.lineCap = 'round';
        paint(g);
        gl.activeTexture(gl.TEXTURE3);
        gl.bindTexture(gl.TEXTURE_2D, o.tex);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);   /* BG1 (2): premultiplied, see SPR_FS */
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, o.cv);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, S.texField);
        o.art = art;
      }
      drawObj(o, mx, my, flat || 0, off, top);
    }
    function drawObj(o, mx, my, flat, off, top) {
      var ox = off && !flat ? off[0] * S.dpr : 0, oy = off && !flat ? off[1] * S.dpr : 0;
      var bx = o.bx, key = mx + ',' + my + ',' + S.dpr + ',' + (flat || 0) + ',' + ox + ',' + oy;
      if (key !== o.key) {
        o.key = key;
        gl.bindBuffer(gl.ARRAY_BUFFER, o.buf);
        gl.bufferData(gl.ARRAY_BUFFER, flat ? new Float32Array([
          mx, my, 3, 0,
          bx.x0 * flat, bx.y0 * flat, (bx.x1 - bx.x0) * flat, (bx.y1 - bx.y0) * flat,
          0, 0, 1, 1]) : new Float32Array([
          mx, my, 0, 0,
          bx.x0 * bx.k * S.dpr + ox, bx.y0 * bx.k * S.dpr + oy,
          (bx.x1 - bx.x0) * bx.k * S.dpr, (bx.y1 - bx.y0) * bx.k * S.dpr,
          0, 0, 1, 1]), gl.DYNAMIC_DRAW);
      }
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.depthMask(false);
      gl.useProgram(pSpr);
      gl.bindVertexArray(o.vao);
      gl.uniform1i(pSpr.u.uField, 0);
      gl.activeTexture(gl.TEXTURE3); gl.bindTexture(gl.TEXTURE_2D, o.tex);
      gl.uniform1i(pSpr.u.uAtlas, 3);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, S.texField);
      H.setCommon(gl, pSpr);
      if (pSpr.u.uRise) gl.uniform1f(pSpr.u.uRise, S.markFade === undefined ? 1 : S.markFade);
      gl.uniform2f(pSpr.u.uVpx, S.cv.width / (S.capK || 1), S.cv.height / (S.capK || 1));   /* PK1 */
      gl.uniform2f(pSpr.u.uFade, 1, 1);
      var dt = top && gl.isEnabled(gl.DEPTH_TEST);
      if (dt) gl.disable(gl.DEPTH_TEST);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, 1);
      if (dt) gl.enable(gl.DEPTH_TEST);
      gl.depthMask(true);
      gl.disable(gl.BLEND);
      gl.bindVertexArray(null);
      stats.draws++;
    }

    cutRose();
    return {
      setMarks: function (M) { setMarks(M); wirePlates(); },
      draw: draw,
      drawDyn: drawDyn,
      drawArt: drawArt,
      fade: fade,                     /* LH2: day.js asks which generation is on */
      invalidate: function () {
        layoutSprites(true); wirePlates();
        for (var k in dynS) if (dynS[k]) dynS[k].key = '';
      },
      stats: function () { return stats; }
    };
  }
  MK.attach = attach;
  MK.precompile = programs;

  MK.place = place;
  MK.segmentsAt = segmentsAt;
})(typeof self !== 'undefined' ? self : this);
